'use client';

import { useEffect, useLayoutEffect, useRef, useCallback, useReducer, useState, useMemo, useEffectEvent } from 'react';
import maplibregl from 'maplibre-gl';
import { ensureRtlTextPlugin } from '@/lib/maplibre-rtl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { toast } from 'sonner';
import { useTheme } from 'next-themes';
import type { LiveDrone } from '@/hooks/useWebSocket';
import { api } from '@/lib/api';
import type { AirspaceNotamItem, AirspaceTfrItem } from '@/components/dashboard/AirspacePanel';
import { AirspacePanel } from '@/components/dashboard/AirspacePanel';
import { NfzIsraelPanel } from '@/components/dashboard/NfzIsraelPanel';
import { buildNfzZonePopup, nfzCaution, prepareNfzIsrael, uniqueZones, type NfzIsraelResponse } from '@/lib/nfz-israel';
import { DroneMapControls, type SelectedDroneMapControls } from '@/components/dashboard/DroneMapControls';
import { DroneMapContextMenu } from '@/components/dashboard/DroneMapContextMenu';
import { TriggerPointCard } from '@/components/dashboard/TriggerPointCard';
import { DroneHoverChip } from '@/components/dashboard/DroneHoverChip';
import { anchoredCardPosition } from '@/lib/anchored-card';
import { stageCentreShiftPx } from '@/app/(dashboard)/_cop/CopStageInsets';
import { buildSourceUpdates, rangeRingAnchor, stalePulseIds } from './droneMapGeoJson';
import {
  addMapDataLayers,
  applyAltitudeLabelStyle,
  applyImageryBrightness,
  IMAGERY_LAYER_ID,
  forceEnglishLabels,
  NFZ_HIT_LAYER_IDS,
  NFZ_SOURCE_ID,
  setAirspaceLayerVisibility,
  setNfzZoneLayerVisibility,
  setMapInteractionCursor,
  setMissionLayerVisibility,
  startPlanActiveLegAnimation,
  SELECTION_BRACKET_LAYER_IDS,
} from './droneMapLayers';
import {
  FALLBACK_MAP_STYLE,
  getPersistedMapStyle,
  initialMapUiState,
  mapUiReducer,
  persistMapStyle,
  styleRequestOutcome,
} from './droneMapReducer';
import { isSatelliteAvailable } from '@/lib/basemap-preference';
import {
  appendAutoTrailPoints,
  appendLiveTrailPoints,
  loadFlightTrailHistory,
} from './droneMapTrails';
import { MAP_LAYERS_MENU } from '@/lib/copy';
import { useAuth } from '@/hooks/useAuth';
import {
  ALL_MAP_ASSETS_VISIBLE,
  mapAssetsHeldBySelection,
  readMapAssetVisibility,
  toggleMapAsset,
  writeMapAssetVisibility,
  type MapAssetKind,
} from '@/lib/map-asset-filters';
import {
  STYLES,
  basemapTransformRequest,
  OFFLINE_FALLBACK_GROUND,
  offlineFallbackStyle,
  type MapStyleKey,
  EMPTY_COOPERATIVE,
  EMPTY_GEOFENCES,
  EMPTY_CONTROLLERS,
  EMPTY_SITES,
  EMPTY_ZONE_DRAW_POINTS,
  EMPTY_MISSIONS,
  EMPTY_PLAN_WAYPOINTS,
  EMPTY_MANUAL_HOLDS,
  EMPTY_TRIGGERS,
  type DroneMapProps,
  type CooperativeTraffic,
  type GeofenceOverlay,
  type RcController,
  type SiteMarker,
  type DroneMapMission,
  type DockMarker,
  type PlanRoutePoint,
  type TriggerMarker,
} from './droneMapTypes';
import { DEFAULT_ZONE_COLOR } from './zones/zoneColors';
import { EMPTY_HANDOVER_LINKS } from './handoverLink';
import {
  IDLE_MAP_MODE,
  isMapModeArmed,
  isPlanPlacementMode,
  mapModeClaimsAssetClicks,
  mapModeParksClicks,
  zoneDrawShape,
  type MapMode,
} from '@/lib/map-mode';
import { NO_MEASUREMENTS } from '@/lib/range-bearing';


/**
 * The mode's page ground, read from the token so the zero-network fallback paints
 * the same colour as the chrome around it in DAY and in NIGHT. Falls back to the
 * NIGHT ground where there is no document or the token is unset.
 */
function themeGround(): string {
  if (typeof document === 'undefined') return OFFLINE_FALLBACK_GROUND;
  const value = getComputedStyle(document.documentElement).getPropertyValue('--background').trim();
  return value || OFFLINE_FALLBACK_GROUND;
}

export type {
  CooperativeTraffic,
  GeofenceOverlay,
  RcController,
  SiteMarker,
  DroneMapMission,
  DockMarker,
  PlanRoutePoint,
  TriggerMarker,
};

// Lazily initialises a ref's value on first render so the Set/Map is not
// recreated on every render. Returns a ref whose `.current` is always non-null.
function useLazyRef<T>(init: () => T): { current: T } {
  const ref = useRef<T | null>(null);
  if (ref.current === null) {
    ref.current = init();
  }
  return ref as { current: T };
}

export const PLAN_MODE_DOUBLE_CLICK_DELAY_MS = 250;

/** How long after a LOOK click a click is its double-click's echo, ms - an OS double-click interval. */
export const LOOK_CLICK_ECHO_MS = 500;

/**
 * How long to wait for the CDN basemap style before treating it as failed.
 * Deliberately generous: this is a wall-clock budget for a cold cache on a slow
 * or distant link, and expiring it early replaces a basemap that was about to
 * load (issue #64). The operator is never blocked while it runs - the fallback
 * renders underneath the fleet the moment the first attempt fails.
 */
export const BASEMAP_LOAD_TIMEOUT_MS = 10_000;

/**
 * Backoff before each automatic re-attempt of the real basemap. Length of the
 * array is the retry count; after the last one we stay on the offline fallback
 * until the operator retries by hand.
 */
export const BASEMAP_RETRY_DELAYS_MS = [3_000, 10_000] as const;

/**
 * Consecutive imagery-tile errors that mean the imagery is gone, not unlucky.
 *
 * A screenful is roughly a dozen tiles, so eight in a row cannot be a couple of
 * stragglers on a fast pan - and imagery tiles all come from one authenticated
 * origin, so when they fail they fail together. A good style load resets the
 * count, which is what stops a slow accumulation from ever reaching this.
 */
export const IMAGERY_ERRORS_BEFORE_OFFLINE = 8;

/**
 * Does this MapLibre 'error' event mean the basemap style itself is
 * unreachable?
 *
 * MapLibre reports every failed sub-resource through the same 'error' channel,
 * and a basemap load fires dozens of them (one per tile, glyph range, sprite).
 * Errors that name a `sourceId` come from a source/tile fetch: the style parsed
 * fine and one request under it failed, which is normal on a flaky link and
 * must never blank the map. Treating those as "CDN unreachable" is what made
 * issue #64 reproduce for some operators and never for others.
 *
 * Only an unattributed error, arriving before the basemap has loaded, indicates
 * the style itself could not be fetched or parsed.
 */
export function isBasemapStyleFailure(
  event: { sourceId?: string } | undefined,
  basemapLoaded: boolean,
): boolean {
  if (basemapLoaded) return false;
  return !event?.sourceId;
}

/**
 * Zoom difference below which two manual calibrations are the same camera.
 *
 * `calibrateSurface` re-derives the zoom from the surface size AND the
 * aircraft's latitude, so the last decimal moves on every telemetry frame while
 * the ring has not changed at all: 1e-4 of zoom is about 700 m of northing.
 * 0.05 is ~3.5% of scale — far below the 0.16 that separates the full map from
 * the 280 px control minimap, and far above anything a moving aircraft produces.
 */
const MANUAL_ZOOM_EPSILON = 0.05;

export function sameManualCalibration(a: number | null, b: number | null): boolean {
  if (a === null || b === null) return a === b;
  return Math.abs(a - b) < MANUAL_ZOOM_EPSILON;
}

/**
 * Pixels of anchor displacement below which two manual focus requests are the
 * same camera.
 *
 * The control surface anchors itself in a bottom corner on a touch pointer, and
 * asks the map to hold the aircraft under that anchor rather than in the middle
 * (`manualControlAnchor`). The offset is derived from the measured surface, so
 * it moves by a fraction of a pixel as the box settles; 1 px is below anything
 * an operator can see and far below the change a handedness switch produces.
 */
const MANUAL_OFFSET_EPSILON_PX = 1;

export function sameManualOffset(
  a: readonly [number, number],
  b: readonly [number, number],
): boolean {
  return (
    Math.abs(a[0] - b[0]) < MANUAL_OFFSET_EPSILON_PX
    && Math.abs(a[1] - b[1]) < MANUAL_OFFSET_EPSILON_PX
  );
}

/**
 * The camera gestures a manual session has to hold still, structurally typed
 * so the rule can be tested without a MapLibre instance.
 *
 * Every one is optional: MapLibre creates them from the constructor options, and
 * a map built with `dragRotate: false` simply has nothing to disable.
 */
export interface RotationLockableMap {
  dragRotate?: { enable(): void; disable(): void };
  touchZoomRotate?: { enableRotation(): void; disableRotation(): void };
  keyboard?: { enableRotation(): void; disableRotation(): void };
  touchPitch?: { enable(): void; disable(): void };
  getBearing?(): number;
  setBearing?(bearing: number): void;
  getPitch?(): number;
  setPitch?(pitch: number): void;
}

/**
 * Hold the basemap north-up and flat for the whole of a manual SESSION, and give
 * rotation and pitch back when the session ends.
 *
 * Bearing: the control surface converts a drag into a COMPASS bearing on the
 * assumption that screen-up is north. `dragRotate` and `touchZoomRotate` are on
 * by default, so a right-drag — or a two-finger tablet twist — would turn the
 * basemap while the surface went on reading screen-up as north. The drawn
 * vector, the BRG readout and the aircraft's actual track would then differ by
 * exactly the rotation, silently, at the moment the operator is reaching back
 * for the halo.
 *
 * Pitch: `targetFromPointer` converts a FLY TO HERE press into ground metres
 * with one flat metres-per-pixel. On a pitched map that scale is wrong
 * everywhere but the centre line, so the dropped pin resolves to a lat/lng the
 * operator did not press — and that leaves as a real `goto`.
 *
 * The lock follows the SESSION, not the camera hold. Releasing the camera hold
 * (a pan, an 8-second look at a rooftop) stops the map FOLLOWING the aircraft
 * and nothing more: the surface is still live and still reading screen-up as
 * north for every one of those 8 seconds, so rotation must stay disabled
 * through them. Nothing is lost — an operator cannot usefully re-orient the
 * basemap while flying off it anyway.
 *
 * `applyManualFocus` already eases to `bearing: 0, pitch: 0` on entry, but an
 * ease can be interrupted by the operator's own pan, so both are also snapped
 * here: the lock is the guarantee, the ease is only the animation.
 */
/**
 * What one `manualFocus` message is asking the map to do.
 *
 * Two facts, deliberately separate, because they are separate questions and
 * conflating them is what let the basemap rotate under a live control surface:
 *
 * - `droneId` — hold the camera on this aircraft, or (null) stop following. The
 *   operator gives this up every time they pan away for a look, and gets it back
 *   from RECENTER or from the 8-second timer.
 * - `sessionActive` — somebody is flying. True for the WHOLE session, including
 *   every second the camera has been let go. This is what gates rotation and
 *   pitch.
 */
export interface ManualFocusIntent {
  droneId: string | null;
  sessionActive: boolean;
  zoom: number | null;
  offset: [number, number];
}

/**
 * Read a `manualFocus` detail off the window-event channel, defensively — it
 * crosses a `CustomEvent` boundary, so nothing about its shape is typed.
 *
 * A sender that states only `droneId` is taken at its word: before the two facts
 * were split apart the camera hold was the only signal there was, so falling
 * back to it reproduces exactly the old meaning rather than silently deciding
 * no session is running.
 */
export function readManualFocusDetail(detail: unknown): ManualFocusIntent {
  const d = (detail ?? {}) as {
    droneId?: unknown;
    sessionActive?: unknown;
    zoom?: unknown;
    offsetXPx?: unknown;
    offsetYPx?: unknown;
  };
  const droneId = typeof d.droneId === 'string' ? d.droneId : null;
  return {
    droneId,
    sessionActive: typeof d.sessionActive === 'boolean' ? d.sessionActive : droneId !== null,
    zoom: typeof d.zoom === 'number' ? d.zoom : null,
    offset: [
      Number.isFinite(d.offsetXPx) ? (d.offsetXPx as number) : 0,
      Number.isFinite(d.offsetYPx) ? (d.offsetYPx as number) : 0,
    ],
  };
}

export function setManualRotationLock(map: RotationLockableMap | null, locked: boolean): void {
  if (!map) return;
  if (locked) {
    map.dragRotate?.disable();
    map.touchZoomRotate?.disableRotation();
    map.keyboard?.disableRotation();
    map.touchPitch?.disable();
    if ((map.getBearing?.() ?? 0) !== 0) map.setBearing?.(0);
    if ((map.getPitch?.() ?? 0) !== 0) map.setPitch?.(0);
    return;
  }
  map.dragRotate?.enable();
  map.touchZoomRotate?.enableRotation();
  map.keyboard?.enableRotation();
  map.touchPitch?.enable();
}

/**
 * The camera options for going to an entity the operator picked.
 *
 * ATAK's verb is "pan to", and it is never "zoom to": selecting an item pans
 * the map to it and leaves the scale alone, and the only thing that rescales
 * the picture is the operator or a lock they engaged themselves. That is the
 * rule this returns. Zoom is operator state - it is the answer to "how much
 * ground am I watching", and a selection is not a request to change that
 * answer.
 *
 * So there is no `zoom` key here, deliberately, and that absence is the whole
 * point: MapLibre keeps the current zoom for any key `flyTo` is not given.
 * Handing it `zoom: 15` while the operator sat at 18 is what made clicking an
 * aircraft to see its picture throw away the view they had clicked FROM.
 *
 * Framing the camera at a chosen scale is a different job with different call
 * sites, and they keep their literals: the first-site fix on load, My Location,
 * and manual flight, which owns the camera outright and holds the aircraft at
 * the zoom that makes its speed ring mean four seconds of travel.
 */
export function panCameraOptions(
  lng: number,
  lat: number,
  offset: [number, number],
  duration: number,
): { center: [number, number]; offset: [number, number]; duration: number } {
  return { center: [lng, lat], offset, duration };
}

export function createPlanModeClickArbiter(delayMs = PLAN_MODE_DOUBLE_CLICK_DELAY_MS) {
  let pending: number[] = [];

  /**
   * Drop every parked click, and report HOW MANY were dropped.
   *
   * The count is the double-click handler's only timing-independent evidence
   * about its own two clicks: two still parked means neither has landed and the
   * route is genuinely untouched by this gesture, whatever React has committed
   * (see planDoubleClickAction).
   */
  const cancel = (): number => {
    const dropped = pending.length;
    pending.forEach((id) => window.clearTimeout(id));
    pending = [];
    return dropped;
  };

  return {
    queueAdd(add: () => void) {
      const id = window.setTimeout(() => {
        pending = pending.filter((pendingId) => pendingId !== id);
        add();
      }, delayMs);
      pending.push(id);
    },
    cancel,
  };
}

/**
 * What a double-click on the map means.
 *
 * 'exit' — the "done placing points" gesture, and the right answer for it, but
 * only once there is something to be done WITH. On an empty COMPOSE route it
 * meant the opposite of what the operator did: the arbiter cancelled both
 * parked clicks, the mode disarmed, and a mission just started came out with
 * zero waypoints and no way back (issue #641).
 *
 * 'place-first' — read the gesture literally instead: they clicked where they
 * want their first point, twice. Place it once and stay armed. Both clicks land
 * on the same spot, so there is no ambiguity about which one to honour.
 *
 * Two things narrow that second answer, and both are deliberate.
 *
 * COMPOSE ONLY. The other two placement modes already handle an empty-route
 * double-click on their own terms and are left exactly as they were: the dock
 * launch overlay re-arms drawing (`DockLaunchOverlay`), and route revision has
 * carried its own re-arm control since it was built (`CopMissionEdit`). A dock
 * LAUNCH route is not the place to introduce a waypoint the operator did not
 * ask for.
 *
 * PARKED CLICKS, not just the count. `waypointCount` comes from React-committed
 * props, and clicks arrive faster than React commits — on a double-click either
 * side of the arbiter's window, the first click's add can have landed while the
 * count still reads 0, and placing "the first point" would then append a
 * duplicate on top of it. `parkedAdds` is the synchronous truth: two clicks
 * still parked means neither has landed yet.
 */
export type PlanDoubleClickAction = 'exit' | 'place-first';

export function planDoubleClickAction({ mode, waypointCount, parkedAdds }: {
  mode: MapMode;
  waypointCount: number;
  parkedAdds: number;
}): PlanDoubleClickAction {
  if (mode.kind !== 'mission-compose') return 'exit';
  if (waypointCount > 0) return 'exit';
  return parkedAdds >= 2 ? 'place-first' : 'exit';
}

export function canDragPlanWaypoints(planEditable: boolean, onPlanWaypointDrag?: (index: number, lat: number, lng: number) => void) {
  return planEditable && !!onPlanWaypointDrag;
}

/** Stable identity, so the destructuring default does not re-run effects. */
const NO_CHROME_INSETS = Object.freeze({ top: 0, right: 0, bottom: 0, left: 0 });

/** The NFZ-Israel zones answer is ~500 KB; a request still open after this is a fault, not a slow line. */
const NFZ_FETCH_TIMEOUT_MS = 20_000;
/** How often an open COP re-asks for the NFZ-Israel zones while the layer is on (a 304 when unchanged). */
const NFZ_REFRESH_MS = 30 * 60_000;

/** Every drawn part of a measurement a right-click can land on to open its Delete menu. */
const MEASURE_HIT_LAYERS = [
  'range-bearing-casing',
  'range-bearing-points',
  'range-bearing-point-labels',
  'range-bearing-label',
];

function useDroneMapController({
  compact = false,
  drones,
  selectedDroneId,
  chromeInsets: chromeInsetsProp = NO_CHROME_INSETS,
  manualHolds = EMPTY_MANUAL_HOLDS,
  onSelectDrone,
  selectedDroneOverlay,
  cooperativeTraffic = EMPTY_COOPERATIVE,
  geofences = EMPTY_GEOFENCES,
  controllers = EMPTY_CONTROLLERS,
  sites = EMPTY_SITES,
  docks = [],
  selectedDockId = null,
  onSelectDock,
  triggers = EMPTY_TRIGGERS,
  onDispatchFromTrigger,
  onDismissTrigger,
  onApproveTrigger,
  onRejectTrigger,
  mapMode = IDLE_MAP_MODE,
  onDispatchTargetPicked,
  onRequestDispatchHere,
  onGotoPointPicked,
  onOpenVideo,
  onOpenZones,
  videoBeside = null,
  lookHere = null,
  rangeBearing = null,
  zoneDrawPoints = EMPTY_ZONE_DRAW_POINTS,
  zonePolygonClosed = false,
  onZoneMapClick,
  zoneCircleCenter,
  zoneCircleRadius = 0,
  zoneDrawColor = DEFAULT_ZONE_COLOR,
  missions = EMPTY_MISSIONS,
  selectedMissionId = null,
  hoveredMissionId = null,
  handoverLinks = EMPTY_HANDOVER_LINKS,
  onSelectMission,
  planWaypoints = EMPTY_PLAN_WAYPOINTS,
  planOrbitRadiusM = null,
  planHome = null,
  planEditable = false,
  onPlanModeExit,
  planSelectedIndex = null,
  planActiveIndex = null,
  onPlanMapClick,
  onPlanWaypointClick,
  onPlanWaypointDrag,
}: DroneMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  // A state mirror of mapRef, purely so children can depend on the map's
  // existence. Child effects run before the parent's, so a child handed only
  // the ref would always see null on mount and never re-subscribe.
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null);
  const sourceReady = useRef(false);
  /** The last NFZ-Israel zones loaded, prepared for drawing - re-applied after a basemap switch. */
  const nfzIsraelData = useRef<GeoJSON.FeatureCollection | null>(null);
  /** The open NFZ-Israel zone card, so the layer going off, a basemap switch, Escape and unmount close it. */
  const nfzPopupRef = useRef<maplibregl.Popup | null>(null);
  /**
   * Whether an aircraft or dock is selected, for the mount-time click handler. A click on empty
   * map with something selected is a DESELECT, and opening a zone card on top of it got in the
   * way of the COP's most common gesture (slice 2 review, product finding 2).
   */
  const hasSelectionRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const styleLoadingRef = useRef(false);
  /** Set by the map-init effect. Lets the offline indicator's retry control and
   *  the style toggle drive the basemap load state machine that lives in that
   *  effect's closure. */
  const retryBasemapRef = useRef<(() => void) | null>(null);
  const rearmBasemapWatchRef = useRef<(() => void) | null>(null);
  const hiddenControllerLinks = useLazyRef<Set<string>>(() => new Set());
  const trailHistory = useLazyRef<Map<string, [number, number][]>>(() => new Map());
  const autoTrailHistory = useLazyRef<Map<string, [number, number][]>>(() => new Map());
  const loadedFlightTrails = useLazyRef<Set<string>>(() => new Set());
  const stalePulseMarkersRef = useLazyRef<Map<string, maplibregl.Marker>>(() => new Map());
  const hasFlewToSite = useRef(false);
  const prevSelectedRef = useRef<string | null>(null);
  const myLocationMarkerRef = useRef<maplibregl.Marker | null>(null);
  /** Aircraft the manual control surface is centred on, or null. */
  const manualFocusIdRef = useRef<string | null>(null);
  /** Zoom that focus was last applied at, so a re-scaled ring is re-calibrated. */
  const manualFocusZoomRef = useRef<number | null>(null);
  /**
   * Where the aircraft is held relative to the container centre, px. Non-zero
   * only when the control surface has anchored itself in a corner for a
   * two-handed grip; the per-tick tracking has to use the same one.
   */
  const manualFocusOffsetRef = useRef<[number, number]>([0, 0]);
  /**
   * Whether a manual session is live at all — which is NOT the same question as
   * whether the camera is tracking the aircraft. The operator can release the
   * camera for a look while still holding the sticks, and rotation/pitch have to
   * stay locked across that whole window (see `setManualRotationLock`).
   */
  const manualSessionActiveRef = useRef(false);

  const onSelectDroneRef = useRef(onSelectDrone);
  onSelectDroneRef.current = onSelectDrone;
  const onSelectDockRef = useRef(onSelectDock);
  onSelectDockRef.current = onSelectDock;
  // Same reason as the two above: the trigger click handler is bound once, for
  // the life of the map, and must read today's rows rather than the ones
  // captured when it was registered.
  const triggersRef = useRef(triggers);
  triggersRef.current = triggers;
  /**
   * The one armed mode, as a ref so the map's long-lived event handlers read
   * the current value without being torn down and rebound on every change.
   * There is exactly one of these now: the four booleans it replaces
   * (`pickDispatchTargetRef`, `gotoPickActiveRef`, `planModeRef`,
   * `zoneDrawModeRef`) could each be true independently, and every handler had
   * to re-derive the priority between them.
   */
  const mapModeRef = useRef(mapMode);
  mapModeRef.current = mapMode;
  const zoneDrawMode = zoneDrawShape(mapMode);
  const planPlacementActive = isPlanPlacementMode(mapMode);
  const onDispatchTargetPickedRef = useRef(onDispatchTargetPicked);
  onDispatchTargetPickedRef.current = onDispatchTargetPicked;
  const onRequestDispatchHereRef = useRef(onRequestDispatchHere);
  onRequestDispatchHereRef.current = onRequestDispatchHere;
  const onGotoPointPickedRef = useRef(onGotoPointPicked);
  onGotoPointPickedRef.current = onGotoPointPicked;
  const onZoneMapClickRef = useRef(onZoneMapClick);
  onZoneMapClickRef.current = onZoneMapClick;
  const onRangeBearingMapClickRef = useRef(rangeBearing?.onMapClick);
  onRangeBearingMapClickRef.current = rangeBearing?.onMapClick;
  const onRangeBearingFinishLineRef = useRef(rangeBearing?.onFinishLine);
  onRangeBearingFinishLineRef.current = rangeBearing?.onFinishLine;
  const onRangeBearingDeleteLineRef = useRef(rangeBearing?.onDeleteLine);
  onRangeBearingDeleteLineRef.current = rangeBearing?.onDeleteLine;
  const onLookHereMapClickRef = useRef(lookHere?.onMapClick);
  onLookHereMapClickRef.current = lookHere?.onMapClick;
  /**
   * Until when a click is the second half of a LOOK click's double-click.
   *
   * The arbiter parks a click for 250 ms, but an OS double-click is up to
   * ~500 ms. A slower one fired the first click - which sent the tilt and put
   * the map back to idle - and then landed its second click on the idle map,
   * where it deselected the aircraft, or selected whatever was under it, and
   * with the selection went the ring and the command's ack (slice 3 review).
   */
  const lookClickEchoUntilRef = useRef(0);
  /** Where the last LOOK sent the camera; destructured for the setData deps, like the ruler's lines. */
  const lookTarget = compact ? null : lookHere?.target ?? null;
  /** Whether a measurement line is still taking points - decides what a double-click means. */
  const rangeBearingDrawingRef = useRef(false);
  rangeBearingDrawingRef.current = rangeBearing?.measurements.drawing ?? false;
  /**
   * Destructured rather than depended on as a whole: `rangeBearing` is one
   * object carrying a callback as well as the lines, so keying the setData
   * effect on the object would re-run every source update whenever the page
   * re-created the handler. `NO_MEASUREMENTS` is a module constant, so a map
   * with no tool has a stable identity here forever.
   *
   * NOT DRAWN ON THE COMPACT MINIMAP, and that is a correctness rule rather
   * than a taste one. The control column is suppressed wholesale when
   * `compact` (a 280 px box has no room for it), and the column is where both
   * the ruler key and CLEAR live - so drawing the measurement there would put
   * ink on a map with no control that can take it off. Since D10 the points
   * outlive the armed mode, so without this an operator who measured something
   * and then took manual control with the video layout would be left with a
   * pink line they could not remove until the session ended. The points
   * themselves are untouched: the measurement comes back with the full map.
   */
  const rangeBearingLines = compact
    ? NO_MEASUREMENTS.lines
    : rangeBearing?.measurements.lines ?? NO_MEASUREMENTS.lines;
  const planEditableRef = useRef(planEditable);
  planEditableRef.current = planEditable;
  const onPlanMapClickRef = useRef(onPlanMapClick);
  onPlanMapClickRef.current = onPlanMapClick;
  const onPlanModeExitRef = useRef(onPlanModeExit);
  onPlanModeExitRef.current = onPlanModeExit;
  // How much route exists right now. The double-click handler is bound once, on
  // map load, so it cannot read the prop — and what the gesture MEANS depends on
  // whether anything has been placed yet (see planDoubleClickAction).
  const planWaypointCountRef = useRef(planWaypoints.length);
  planWaypointCountRef.current = planWaypoints.length;
  const onPlanWaypointClickRef = useRef(onPlanWaypointClick);
  onPlanWaypointClickRef.current = onPlanWaypointClick;
  const onPlanWaypointDragRef = useRef(onPlanWaypointDrag);
  onPlanWaypointDragRef.current = onPlanWaypointDrag;
  const onSelectMissionRef = useRef(onSelectMission);
  onSelectMissionRef.current = onSelectMission;
  // Set while a plan marker is being dragged so the click that MapLibre fires
  // on mouseup does not drop an extra waypoint.
  const planDragMovedRef = useRef(false);
  const planModeClickArbiterRef = useLazyRef(() => createPlanModeClickArbiter());
  const dronesRef = useRef(drones);
  dronesRef.current = drones;
  const sitesRef = useRef(sites);
  sitesRef.current = sites;

  const [ui, dispatch] = useReducer(
    mapUiReducer,
    initialMapUiState,
    (base) => ({ ...base, mapStyle: getPersistedMapStyle() }),
  );
  const uiRef = useRef(ui);
  uiRef.current = ui;

  /**
   * The chrome floating over the map, or nothing at all in fullscreen.
   *
   * Fullscreen is taken on THIS wrapper (`toggleFullscreen`), so none of the
   * COP's chrome is painted over it: not the bottom stack, and not the side
   * panel, which is an ancestor's sibling and 288 to 560 px wide. Honouring an
   * inset for a panel that is not on screen pushes the map's key column that far
   * in from a screen edge nothing is over - and the control that LEAVES
   * fullscreen is in that column, so the operator's way out would be the thing
   * that moved. The anchored chip would be clamped the same way.
   */
  const chromeInsets = ui.isFullscreen ? NO_CHROME_INSETS : chromeInsetsProp;

  /**
   * Where the centre of the VISIBLE map is, relative to the centre of the canvas.
   *
   * Every camera move below says "put this here" and means the middle of the map
   * the operator is looking at. That used to be the same point as the middle of
   * the canvas, because a panel opening NARROWED the map - so none of them ever
   * had to say so. The side panel floats now, the canvas runs underneath it, and
   * the two points are `insets.right / 2` apart: 160 px at the default width,
   * 280 px at the maximum.
   *
   * Left uncorrected, clicking an aircraft in the strip board settles it 160 px
   * right of centre, and FOLLOW - which re-centres on every telemetry tick -
   * parks the tracked aircraft against the panel's edge for the whole sortie.
   *
   * A ref because the window-event handlers below are registered once with `[]`
   * deps and would otherwise close over the insets as they were at mount. Set
   * during render, the same way `uiRef` above is.
   */
  const centreShift = stageCentreShiftPx(chromeInsets);
  const cameraOffsetRef = useRef<[number, number]>([centreShift.dxPx, centreShift.dyPx]);
  cameraOffsetRef.current = [centreShift.dxPx, centreShift.dyPx];

  // getPersistedMapStyle() above already resolves the real localStorage value
  // during the initial client render, but the server always renders the
  // 'dark' default (no access to localStorage) — mismatched values would hit
  // any JSX that renders differently per mapStyle (the day/night toggle icon)
  // as a hydration error. Gate that JSX behind `hydrated` so both the server
  // and the first client render agree, then swap in the real value post-mount.
  const [hydrated, setHydrated] = useState(false);

  /**
   * Whether this deployment can serve imagery, from the auth payload's feature
   * switches. It is NOT probed from here: the map mounts before login completes,
   * so a mount-time read of the token finds nothing, and an empty dependency
   * array means it never asks again. That shipped, and the symptom was an option
   * that silently never appeared on a correctly configured server. Riding on
   * `/api/auth/me` means the answer arrives with the auth it depends on.
   *
   * False until known, so the item is absent rather than offered-then-broken.
   */
  const { features, user } = useAuth();
  const satelliteAvailable = features.satelliteBasemap;
  /** Stable operator identity (email today), so a shared console does not blend two operators' filters. */
  const operator = user?.email ?? null;
  useEffect(() => setHydrated(true), []);

  /**
   * The display mode, for IMAGERY BRIGHTNESS only.
   *
   * Not a return of D3: the mode does not choose which basemap is drawn. It
   * decides how bright a photographic ground may be, which is the difference
   * between NIGHT meaning something on a full-bleed map and NIGHT tinting a few
   * floating panels while daylight imagery fills the screen.
   */
  const { resolvedTheme } = useTheme();
  const displayMode: 'light' | 'dark' = resolvedTheme === 'light' ? 'light' : 'dark';
  const displayModeRef = useRef(displayMode);
  displayModeRef.current = displayMode;

  const applySourcesToMap = useCallback(() => {
    const map = mapRef.current;
    if (!map || !sourceReady.current) return;
    const now = Date.now();
    const updates = buildSourceUpdates({
      drones: dronesRef.current,
      geofences,
      cooperativeTraffic,
      controllers,
      sites,
      missions,
      docks,
      triggers,
      selectedDroneId,
      selectedDockId,
      manualHolds,
      selectedMissionId,
      hoveredMissionId,
      handoverLinks,
      showRangeRings: uiRef.current.showRangeRings,
      // NEVER in the control minimap. `compact` hides the whole control column,
      // so the Layers menu and the hidden-count badge are both absent there -
      // an operator who hid the aircraft on the COP and then switched to the
      // video-dominant manual layout would be hand-flying against the only
      // positional picture they have left, with no aircraft on it and no
      // control in reach to bring it back. An own-aircraft filter has no
      // meaning on a single-aircraft control picture anyway.
      assetVisibility: compact ? ALL_MAP_ASSETS_VISIBLE : uiRef.current.assetVisibility,
      trailDroneIds: uiRef.current.trailDroneIds,
      controllerLinkId: uiRef.current.controllerLinkId,
      zoneDrawMode,
      zoneDrawPoints,
      zonePolygonClosed,
      zoneCircleCenter,
      zoneCircleRadius,
      zoneDrawColor,
      planWaypoints,
      planOrbitRadiusM,
      planHome,
      planSelectedIndex,
      planActiveIndex,
      rangeBearingLines,
      // No coordinate reading on the 280 px control minimap: two lines of it
      // would cover most of the box. The footprint itself stays - it is the
      // one place a hand-flying operator sees where the camera points.
      cameraReading: !compact,
      lookTarget,
      trailHistory: trailHistory.current,
      autoTrailHistory: autoTrailHistory.current,
      now,
    });
    for (const [sourceId, data] of Object.entries(updates)) {
      (map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined)?.setData(data);
    }
  }, [
    compact,
    geofences,
    cooperativeTraffic,
    controllers,
    sites,
    missions,
    docks,
    // Same class of load-bearing: a detection that has just arrived must reach
    // the picture on the broadcast that reported it. On a quiet fleet there is
    // no next telemetry tick to carry it.
    triggers,
    // Load-bearing: without these the selection bracket does not appear until
    // the NEXT telemetry tick re-runs this callback for an unrelated reason.
    selectedDroneId,
    selectedDockId,
    // Same: a hold that has just been taken (or released) must reach the
    // picture on the broadcast that reported it, not on the next tick.
    manualHolds,
    // Same reasoning as the selection ids above: a panel hover or pick changes
    // nothing else, so without these the restyle waits for the next telemetry tick.
    selectedMissionId,
    hoveredMissionId,
    // A handover that has just started (or ended) must reach the picture on the read that
    // reported it, not on the next telemetry tick. The countdown itself rides the 1 Hz tick.
    handoverLinks,
    zoneDrawMode,
    zoneDrawPoints,
    zonePolygonClosed,
    zoneCircleCenter,
    zoneCircleRadius,
    // Also load-bearing: the operator switching colour changes nothing else, so
    // without this the preview keeps the old colour until the next map click.
    zoneDrawColor,
    planWaypoints,
    // Load-bearing, same as the zone colour above: typing a new radius changes nothing
    // else on the map, so without this the ring keeps the old size until the next tick.
    planOrbitRadiusM,
    planHome,
    planSelectedIndex,
    planActiveIndex,
    // Load-bearing in the same way the zone colour above is: a click that
    // places a measurement point changes nothing else on the map, so without
    // this the line and its reading would wait for the next telemetry tick -
    // and on a fleet that is parked there may not be one.
    rangeBearingLines,
    // Same: the ring must appear on the click that sent the camera, and go on
    // the frame the gimbal arrives, not on the next telemetry tick.
    lookTarget,
    trailHistory,
    autoTrailHistory,
  ]);

  const fetchAirspaceData = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !sourceReady.current) return;
    dispatch({ type: 'AIRSPACE_LOAD_START' });
    try {
      const b = map.getBounds();
      const bbox = `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`;
      const c = map.getCenter();
      const [cls, tfr, nm] = await Promise.all([
        api.get<{ data: GeoJSON.FeatureCollection }>(
          `/api/airspace/classes?bbox=${encodeURIComponent(bbox)}`,
        ),
        api.get<{ data: { geojson: GeoJSON.FeatureCollection; items: AirspaceTfrItem[] } }>('/api/airspace/tfrs'),
        api.get<{
          data: {
            notams: AirspaceNotamItem[];
            summary: { clear_to_fly: boolean; status: string };
          };
        }>(
          `/api/airspace/notams?lat=${encodeURIComponent(c.lat)}&lng=${encodeURIComponent(c.lng)}&radius=50`,
        ),
      ]);
      dispatch({
        type: 'AIRSPACE_LOAD_SUCCESS',
        tfrItems: tfr.data.items,
        notamItems: nm.data.notams,
        notamClearToFly: nm.data.summary.clear_to_fly,
      });

      const sCls = map.getSource('airspace-classes') as maplibregl.GeoJSONSource | undefined;
      const sTfr = map.getSource('airspace-tfrs') as maplibregl.GeoJSONSource | undefined;
      const sNm = map.getSource('airspace-notams') as maplibregl.GeoJSONSource | undefined;
      sCls?.setData(cls.data);
      sTfr?.setData(tfr.data.geojson);
      const notamFc: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: nm.data.notams.map((n) => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [n.longitude, n.latitude] },
          properties: { id: n.id, text: n.text },
        })),
      };
      sNm?.setData(notamFc);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Airspace load failed';
      dispatch({ type: 'AIRSPACE_LOAD_ERROR', error: msg });
    }
  }, []);

  const fetchAirspaceOnMove = useEffectEvent(() => {
    void fetchAirspaceData();
  });

  /**
   * The NFZ-Israel zones, whole country in one answer (~500 KB, ETag-revalidated by the browser,
   * so a refetch of an unchanged map is a 304). Kept in a ref as well as on the source: a basemap
   * switch wipes every source, and the zones go back on from here without another request.
   */
  const fetchNfzIsrael = useCallback(async () => {
    dispatch({ type: 'NFZ_ISRAEL_LOAD_START' });
    try {
      const res = await api.get<NfzIsraelResponse>('/api/airspace/nfz-israel', {
        signal: AbortSignal.timeout(NFZ_FETCH_TIMEOUT_MS),
      });
      const { source, geojson } = res.data;
      // The server's answer is the truth either way: no stored copy means nothing drawn, so the
      // map never shows zones under a strip saying none are available (slice 2 review, eng 4).
      nfzIsraelData.current = geojson ? prepareNfzIsrael(geojson) : null;
      const src = mapRef.current?.getSource(NFZ_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      src?.setData(nfzIsraelData.current ?? { type: 'FeatureCollection', features: [] });
      dispatch({ type: 'NFZ_ISRAEL_LOAD_SUCCESS', source });
    } catch (err) {
      dispatch({ type: 'NFZ_ISRAEL_LOAD_ERROR', error: err instanceof Error ? err.message : 'NFZ-Israel zones load failed' });
    }
  }, []);

  const addLayers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    addMapDataLayers(map, {
      mapStyle: uiRef.current.mapStyle,
      showAirspace: uiRef.current.showAirspace,
      showNfzIsrael: uiRef.current.showNfzIsrael,
      showMissions: uiRef.current.showMissions,
    });
    sourceReady.current = true;
    applySourcesToMap();
    if (uiRef.current.showAirspace) void fetchAirspaceData();
    if (nfzIsraelData.current) {
      (map.getSource(NFZ_SOURCE_ID) as maplibregl.GeoJSONSource | undefined)?.setData(nfzIsraelData.current);
    } else if (uiRef.current.showNfzIsrael) {
      void fetchNfzIsrael();
    }
  }, [applySourcesToMap, fetchAirspaceData, fetchNfzIsrael]);

  const runAddLayers = useEffectEvent(() => {
    addLayers();
  });

  const enableTrailForDrone = useCallback((droneId: string, enable: boolean) => {
    if (enable) {
      dispatch({ type: 'TOGGLE_TRAIL', droneId });
      if (!loadedFlightTrails.current.has(droneId)) {
        loadedFlightTrails.current.add(droneId);
        void loadFlightTrailHistory(droneId, trailHistory.current).then(() => {
          applySourcesToMap();
        });
      }
    } else {
      dispatch({ type: 'REMOVE_TRAIL', droneId });
      loadedFlightTrails.current.delete(droneId);
      trailHistory.current.delete(droneId);
      applySourcesToMap();
    }
  }, [applySourcesToMap, loadedFlightTrails, trailHistory]);

  const handleToggleAirspace = useCallback(() => {
    const next = !uiRef.current.showAirspace;
    dispatch({ type: 'TOGGLE_AIRSPACE' });
    const map = mapRef.current;
    if (map?.getLayer('airspace-e-fill')) {
      setAirspaceLayerVisibility(map, next);
    }
    if (next) void fetchAirspaceData();
  }, [fetchAirspaceData]);

  const retryNfzIsrael = useCallback(() => { void fetchNfzIsrael(); }, [fetchNfzIsrael]);

  const closeNfzPopup = useCallback(() => {
    nfzPopupRef.current?.remove();
    nfzPopupRef.current = null;
  }, []);

  useEffect(() => {
    hasSelectionRef.current = Boolean(selectedDroneId || selectedDockId);
  }, [selectedDroneId, selectedDockId]);

  // Escape closes an open zone card - the card is a canvas popup with no keyboard handling of
  // its own (slice 2 review, product finding 5). Only while one is open, and the event is left
  // to bubble, so every other Escape (menus, an armed tool) still gets it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && nfzPopupRef.current) closeNfzPopup();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      closeNfzPopup();
    };
  }, [closeNfzPopup]);

  const handleToggleNfzIsrael = useCallback(() => {
    const next = !uiRef.current.showNfzIsrael;
    dispatch({ type: 'TOGGLE_NFZ_ISRAEL' });
    const map = mapRef.current;
    if (map?.getLayer('nfz-israel-fill')) setNfzZoneLayerVisibility(map, next);
    // A card about zones that are no longer drawn is a card that lies.
    if (!next) closeNfzPopup();
    // Every time it is turned on, not only the first: it is the operator's cue that the picture
    // should be current, and an unchanged map costs a 304.
    if (next) void fetchNfzIsrael();
  }, [fetchNfzIsrael, closeNfzPopup]);

  // A COP is left open for days. While the layer is on, ask again on a slow cadence so a change
  // ICD made (and the server picked up) reaches the screen, and the strip's check time stays true.
  useEffect(() => {
    if (!ui.showNfzIsrael) return;
    const timer = setInterval(() => { void fetchNfzIsrael(); }, NFZ_REFRESH_MS);
    return () => clearInterval(timer);
  }, [ui.showNfzIsrael, fetchNfzIsrael]);

  const handleToggleMissions = useCallback(() => {
    const next = !uiRef.current.showMissions;
    dispatch({ type: 'TOGGLE_MISSIONS' });
    const map = mapRef.current;
    if (map?.getLayer('mission-route-lines')) {
      setMissionLayerVisibility(map, next);
    }
  }, []);

  // A pick from the missions panel promises a visible route. If the operator
  // had toggled the missions layer off, the pick turns it back on — otherwise
  // the camera flies to a stretch of empty map and the promise is silently
  // broken.
  useEffect(() => {
    if (!selectedMissionId || uiRef.current.showMissions) return;
    handleToggleMissions();
  }, [selectedMissionId, handleToggleMissions]);

  /**
   * The operator's stored asset filter, read AFTER mount.
   *
   * Never during render, for the reason `hydrated` above gives: the server has
   * no `localStorage`, so seeding from it during the first render is a
   * hydration mismatch. The first paint therefore always shows everything, and
   * a stored choice replaces it on the next tick - which is the safe direction
   * for a tactical display (a class is shown and then hidden, never the other
   * way round). `SET_MAP_ASSETS` bails out when nothing is hidden, so the
   * common case costs no source rebuild.
   */
  useEffect(() => {
    dispatch({ type: 'SET_MAP_ASSETS', value: readMapAssetVisibility(operator) });
  }, [operator]);

  /**
   * Take one class of asset off the map, or put it back.
   *
   * The REDRAW is not done here. `uiRef.current` is assigned during render, so
   * inside this handler it still holds the pre-toggle filter - calling
   * `applySourcesToMap()` from here would rebuild every source from the value
   * the operator just changed away from, which is a no-op that reads like the
   * thing that did the work. What actually applies it is the source-apply
   * effect below, which lists `ui.assetVisibility` in its deps and therefore
   * runs on this same commit: immediate, and with the new value. The aircraft
   * are docked and still at rest for most of a shift, so waiting for the next
   * telemetry frame was never an option either.
   *
   * `next` is computed only to persist it, for the same reason: the reducer's
   * result is not readable from here yet.
   */
  const handleToggleMapAsset = useCallback((kind: MapAssetKind) => {
    const next = toggleMapAsset(uiRef.current.assetVisibility, kind);
    dispatch({ type: 'TOGGLE_MAP_ASSET', kind });
    // A refused write is not worth a toast over the air picture: the filter
    // still applies for this session, and the only thing lost is that it will
    // not be there next login. It is worth a LINE, though - a locked-down
    // console where the choice silently never persists is otherwise
    // undebuggable from the console output alone.
    if (!writeMapAssetVisibility(next, operator)) {
      console.warn('[cop] map asset filter could not be persisted', { kind, hidden: !next[kind] });
    }
  }, [operator]);

  /**
   * A pick promises a VISIBLE asset, so picking one whose class is hidden turns
   * that class back on.
   *
   * The same rule the missions layer already follows above, and it is here for
   * the same reason - otherwise the camera eases to the coordinates and shows
   * bare ground while the side panel fills with telemetry for something that is
   * not on the picture. Every route into a selection is affected: the alert
   * bell's go-to, the COP search, a strip-board row, the dock roster. Several
   * of them are how an operator answers a critical alert.
   *
   * It also settles three things at once that would otherwise each need their
   * own exemption: manual flight control draws its velocity vector out of the
   * aircraft symbol and is only reachable for a SELECTED aircraft; the
   * selection chip and brackets anchor to a marker; and a dock launch plans its
   * first leg from the dock it is launching out of. None of them can now be
   * entered against something the map is not drawing.
   *
   * No toast: the asset appearing IS the confirmation, exactly as it is for the
   * mission routes.
   *
   * BOTH ORDERS. `ui.assetVisibility` is in the deps, so the promise is
   * re-asserted when the FILTER changes under an existing selection, not only
   * when the selection changes under an existing filter. The pre-merge review
   * (2026-09-23) found the second order uncovered: select an aircraft, then
   * untick Drones in Layers, and the chip, brackets, follow and manual-control
   * halo all stayed anchored to a symbol the map no longer drew - reachable
   * mid hand-flight in the map-dominant manual layout, where the Layers key is
   * in reach. The menu locks those rows (`assetsHeld`) so this is the safety
   * net rather than the ordinary path.
   */
  const assetsHeld = useMemo(
    () => mapAssetsHeldBySelection(selectedDroneId, selectedDockId),
    [selectedDroneId, selectedDockId],
  );
  useEffect(() => {
    for (const kind of assetsHeld) {
      if (!uiRef.current.assetVisibility[kind]) handleToggleMapAsset(kind);
    }
  }, [assetsHeld, ui.assetVisibility, handleToggleMapAsset]);

  const handleToggleAltitudeLabels = useCallback(() => {
    const next = !uiRef.current.showAltitudeLabels;
    dispatch({ type: 'TOGGLE_ALTITUDE_LABELS' });
    const map = mapRef.current;
    if (map) applyAltitudeLabelStyle(map, next);
  }, []);

  const resetNorth = () => { mapRef.current?.easeTo({ bearing: 0, pitch: 0, duration: 500 }); };

  // Center the map on the operator and drop a persistent "you are here" marker.
  // Success is deliberately silent: the camera move and the pulsing marker ARE
  // the confirmation, and the coordinates live on the marker's tooltip - a
  // toast on every press only stacked up over the picture it just centred.
  // Only failure speaks, and every failure path does: denied permission,
  // timeout, unavailable API (never the old silent `() => {}`).
  const goToMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const map = mapRef.current;
        if (!map) return;
        const { longitude, latitude } = pos.coords;
        const label = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
        map.flyTo({
          center: [longitude, latitude],
          offset: cameraOffsetRef.current,
          zoom: 14,
          duration: 1000,
        });
        if (myLocationMarkerRef.current) {
          myLocationMarkerRef.current.setLngLat([longitude, latitude]);
        } else {
          const el = document.createElement('div');
          el.className = 'my-location-marker';
          el.setAttribute('aria-label', 'Your location');
          myLocationMarkerRef.current = new maplibregl.Marker({ element: el, anchor: 'center' })
            .setLngLat([longitude, latitude])
            .addTo(map);
        }
        myLocationMarkerRef.current.getElement().title = `You are here · ${label}`;
      },
      (err) => {
        const msg =
          err.code === err.PERMISSION_DENIED
            ? 'Location permission denied - enable it in your browser to use My Location'
            : err.code === err.TIMEOUT
              ? 'Location request timed out - try again'
              : 'Could not determine your location';
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  // One path for every basemap change: the operator's own pick (persisted, it is a
  // decision) and the automatic move onto this deployment's default (not persisted -
  // nobody chose it, and persisting it would make the next default change unable to
  // reach anyone). Same path, so glyph and bracket re-registration on 'style.load'
  // runs either way (R5).
  // An automatic move that lands while a style is loading is deferred, never dropped
  // (D14): the 'style.load' handler below drains it, because nothing will ask again
  // on the operator's behalf. A button press mid-load is dropped; the operator sees
  // the button and presses again.
  const pendingDefaultStyleRef = useRef<MapStyleKey | null>(null);
  const applyStyle = (next: MapStyleKey, persist: boolean) => {
    // BEFORE the outcome switch, deliberately. Picking the basemap already
    // showing is a no-op for the MAP and is not a no-op for the RECORD: it is
    // the operator saying "this one, keep it", and it is the only way they can
    // say it. Left below the switch, an operator on the fallback who presses
    // Dark to keep it stores nothing, `hasStoredMapStyle()` stays false, and the
    // deployment default moves the ground under them anyway.
    if (persist) persistMapStyle(next);
    const outcome = styleRequestOutcome(styleLoadingRef.current, uiRef.current.mapStyle, next, !persist);
    if (outcome === 'defer') {
      // The TARGET, not a flag: the drain used to re-derive it from storage,
      // which is only ever correct by coincidence.
      pendingDefaultStyleRef.current = next;
      return;
    }
    if (outcome === 'noop') return;
    dispatch({ type: 'SET_MAP_STYLE', value: next });
    if (mapRef.current) {
      styleLoadingRef.current = true;
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      const bearing = mapRef.current.getBearing();
      // Watch this load too - the target style is a different provider and can
      // fail independently of the one already showing.
      rearmBasemapWatchRef.current?.();
      mapRef.current.setStyle(STYLES[next]);
      // Restoring the camera is all this needs to do. Re-adding layers,
      // English labels and the basemap-offline flag are owned by the map's
      // 'style.load' handler, which - unlike this one - can tell a real style
      // from the offline fallback. Clearing the flag here would mark the
      // basemap "online" on the very load that fell back to offline.
      mapRef.current.once('style.load', () => {
        styleLoadingRef.current = false;
        const map = mapRef.current!;
        map.setCenter(center);
        map.setZoom(zoom);
        map.setBearing(bearing);
        const pending = pendingDefaultStyleRef.current;
        if (pending) {
          pendingDefaultStyleRef.current = null;
          if (pending !== uiRef.current.mapStyle) applyStyleRef.current(pending, false);
        }
      });
    }
  };
  // A named destination, not a cycle. With two basemaps "the other one" was
  // well defined; with imagery as a third it is not, and a menu whose items
  // cannot say which basemap they select is a menu that picks the wrong one.
  const selectStyle = (next: MapStyleKey) => applyStyle(next, true);
  const applyStyleRef = useRef(applyStyle);
  applyStyleRef.current = applyStyle;

  /**
   * Keep the drawn basemap inside what this deployment can actually serve.
   *
   * The UPWARD direction needs no effect: `layout.tsx` renders
   * `<DashboardInitializing />` until `/api/auth/me` resolves, and `useAuth`
   * writes the availability cache before the state that ends it - so by the time
   * this map first renders, `getPersistedMapStyle()` has already returned
   * imagery. An effect for that case was written first and was dead code with a
   * comment describing a mount order the shell does not allow.
   *
   * DOWNWARD is real and has no other guard. An operator holding `satellite` -
   * their own pick, or the default from when the key worked - meets a deployment
   * whose key was rotated out or whose quota is gone. `MapLayersMenu` then hides
   * the Satellite item, so the map is showing a basemap its own menu no longer
   * lists, every tile 501s, and the offline banner sits there for the whole
   * session while `dark` and `bright` both work perfectly. Move them off it.
   *
   * Not persisted, for the same reason nothing automatic is: the operator's
   * stored choice is left exactly as it was, so imagery comes back on its own
   * the day the key does.
   *
   * The live switch is the TRIGGER and the cache is the AUTHORITY. They differ
   * in exactly one case that matters: `/api/auth/me` failing transiently leaves
   * `features` at all-off with `loading` false (retries exhausted, tokens kept),
   * and taking that as "no imagery" would blank a working satellite map over a
   * network blip. The cache is only ever written from a real answer.
   */
  useEffect(() => {
    if (!hydrated || isSatelliteAvailable()) return;
    if (uiRef.current.mapStyle !== 'satellite') return;
    applyStyleRef.current(FALLBACK_MAP_STYLE, false);
  }, [hydrated, satelliteAvailable]);

  // Pressing DAY or NIGHT re-dims the imagery immediately. `style.load` covers
  // every style that arrives; this covers the mode changing under a style that
  // is already up, which is the case an operator actually experiences when the
  // sun goes down mid-shift.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    applyImageryBrightness(map, displayMode);
  }, [displayMode]);

  // Operator-triggered basemap reload. The offline fallback is a usable
  // tactical picture but not a working map, and automatic retries stop after
  // the configured backoff - so recovery must not require a page reload.
  const retryBasemap = () => {
    if (ui.basemapRetrying) return;
    retryBasemapRef.current?.();
  };

  const toggleFullscreen = () => {
    if (!wrapperRef.current) return;
    if (!document.fullscreenElement) {
      void wrapperRef.current.requestFullscreen();
      dispatch({ type: 'SET_FULLSCREEN', value: true });
    } else {
      void document.exitFullscreen();
      dispatch({ type: 'SET_FULLSCREEN', value: false });
    }
  };

  // MapLibre initialization effect: creates the map once and attaches its
  // independent event handlers (click / contextmenu / drag / mousemove). The
  // dispatches counted here live in separate user-event handlers and never run
  // together as a synchronous cascade - this is external-system setup, not a
  // state cascade. Flagged as a heuristic false positive.
  // react-doctor-disable-next-line react-doctor/no-cascading-set-state
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let airspaceMoveTimer: ReturnType<typeof setTimeout> | null = null;

    const firstSite = sitesRef.current.length > 0 ? sitesRef.current[0] : null;
    const initialCenter: [number, number] = firstSite
      ? [firstSite.longitude, firstSite.latitude]
      : [0, 20];
    const initialZoom = firstSite ? 14 : 2;

    // Register the RTL text plugin before the map parses any labels, so Hebrew
    // place names render right-to-left instead of reversed (#75).
    ensureRtlTextPlugin();

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLES[uiRef.current.mapStyle],
      center: initialCenter,
      zoom: initialZoom,
      attributionControl: false,
      // MapLibre's default sizes this to the viewport, which is tuned for tiles
      // that are cheap to refetch. Ours are not: the box is in us-east-1 and the
      // operators are not, so a tile that leaves this cache costs a ~600 ms
      // round trip to get back. Holding a few screens' worth means panning back
      // over ground already seen is instant instead of re-fetched. 500 x ~27 KB
      // is ~13 MB of decoded imagery, which is the same order as the drone
      // trails already held.
      maxTileCacheSize: 500,
      // The satellite basemap is served by OUR api, which is bearer-authenticated
      // (headers only - `requireAuth` does not read cookies, and the query-token
      // path is restricted to export/TAK). MapLibre issues its own fetches for the
      // style document and every tile and knows nothing about the session, so the
      // token is attached here or those requests are 401s and the ground never
      // draws. Read from storage per request rather than captured once: the access
      // token is refreshed on expiry and a captured one goes stale mid-shift.
      // CDN basemaps are left untouched - sending a bearer to a third party would
      // hand them the operator's token.
      transformRequest: basemapTransformRequest,
    });

    // MapLibre's trackResize only reacts to window resize events, not to a
    // sibling flex item (e.g. the Dock Sidebar) changing this container's own
    // width. Without this the canvas can go stale/cropped when the sidebar
    // opens, closes, or is dragged to a new width.
    const resizeObserver = new ResizeObserver(() => map.resize());
    resizeObserver.observe(containerRef.current);

    if (!firstSite && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          map.flyTo({
            center: [pos.coords.longitude, pos.coords.latitude],
            offset: cameraOffsetRef.current,
            zoom: 13,
            duration: 1500,
          });
        },
        () => {},
        { timeout: 5000 },
      );
    }

    // The base style loads from an external CDN. If it's genuinely unreachable
    // (ad blocker, restrictive network, CDN outage) MapLibre never finishes
    // loading and the map stays permanently blank, so we recover onto a
    // zero-network fallback style (solid background, no sources) and keep
    // rendering drones/docks on top of it.
    //
    // Two failure modes this deliberately avoids, both of which the earlier
    // version had (issue #64):
    //
    //  1. Falling back on ANY 'error'. MapLibre emits 'error' for every failed
    //     sub-resource - individual tiles, glyph ranges, the sprite - and a
    //     basemap load fires dozens of those requests. Treating one transient
    //     404/429 as "CDN unreachable" blanked a basemap that had loaded
    //     perfectly well, which is why this reproduced for some operators and
    //     never for others. Only an error carrying no sourceId, arriving
    //     before the style has loaded, is a real style failure.
    //
    //  2. Giving up permanently after one short window. A slow link (mobile,
    //     VPN, cold cache, distant region) legitimately takes longer than the
    //     old 4s budget, and the fallback then replaced a style that was about
    //     to succeed - with no way back short of a page reload. Retry with
    //     backoff first, and leave the operator a way to retry by hand.
    let basemapLoaded = false;
    // Consecutive imagery-tile errors since the last good style load.
    let imageryErrors = 0;
    let usingOfflineFallback = false;
    let basemapAttempt = 0;
    let styleLoadTimer: ReturnType<typeof setTimeout> | null = null;
    let basemapRetryTimer: ReturnType<typeof setTimeout> | null = null;

    const clearBasemapTimers = () => {
      if (styleLoadTimer) clearTimeout(styleLoadTimer);
      if (basemapRetryTimer) clearTimeout(basemapRetryTimer);
      styleLoadTimer = null;
      basemapRetryTimer = null;
    };

    const markBasemapLoaded = () => {
      basemapLoaded = true;
      usingOfflineFallback = false;
      basemapAttempt = 0;
      clearBasemapTimers();
      dispatch({ type: 'SET_BASEMAP_OFFLINE', value: false });
    };

    // Reload the real CDN style over whatever is currently displayed. Used by
    // both the automatic backoff and the operator's manual retry.
    const loadRealBasemap = () => {
      if (basemapLoaded) return;
      clearBasemapTimers();
      usingOfflineFallback = false;
      dispatch({ type: 'SET_BASEMAP_RETRYING', value: true });
      styleLoadTimer = setTimeout(() => handleBasemapFailure('timeout'), BASEMAP_LOAD_TIMEOUT_MS);
      map.setStyle(STYLES[uiRef.current.mapStyle]);
    };

    const applyOfflineFallback = () => {
      if (usingOfflineFallback) return;
      usingOfflineFallback = true;
      clearBasemapTimers();
      dispatch({ type: 'SET_BASEMAP_RETRYING', value: false });
      map.setStyle(offlineFallbackStyle(themeGround()) as unknown as maplibregl.StyleSpecification);
      // Surface it so the operator knows WHY the map is plain, instead of a
      // silently blank basemap (drones/docks still render on the fallback).
      dispatch({ type: 'SET_BASEMAP_OFFLINE', value: true });
    };

    function handleBasemapFailure(reason: string, styleAlreadyLoaded = false) {
      // A style that loaded is normally proof the basemap is fine, and a late
      // error is one flaky tile MapLibre will retry. Imagery breaks that
      // assumption: its style document is served by us and loads perfectly while
      // every tile under it fails, so the one caller that has already proven a
      // whole screen of tiles is failing says so and is let through.
      if (basemapLoaded && !styleAlreadyLoaded) return;
      clearBasemapTimers();
      if (basemapAttempt < BASEMAP_RETRY_DELAYS_MS.length) {
        const delay = BASEMAP_RETRY_DELAYS_MS[basemapAttempt];
        basemapAttempt += 1;
        console.warn(
          `[map] basemap load failed (${reason}) - retry ${basemapAttempt}/${BASEMAP_RETRY_DELAYS_MS.length} in ${delay}ms`,
        );
        // Show the fallback while waiting so the operator always has a usable
        // tactical picture, rather than staring at a blank canvas mid-backoff.
        applyOfflineFallback();
        basemapRetryTimer = setTimeout(loadRealBasemap, delay);
        return;
      }
      console.warn(`[map] basemap unreachable (${reason}) - staying on offline fallback`);
      applyOfflineFallback();
    }

    styleLoadTimer = setTimeout(() => handleBasemapFailure('timeout'), BASEMAP_LOAD_TIMEOUT_MS);

    const onMapLoad = () => {
      // 'load' fires once, for whichever style finishes first - and when the
      // real style fails that is the offline fallback we just swapped in.
      // Counting the fallback's own load as success would clear the operator's
      // banner and stop the retries, leaving a blank map with no explanation:
      // exactly the silent degradation this whole path exists to prevent.
      if (!usingOfflineFallback) {
        markBasemapLoaded();
        forceEnglishLabels(map);
      }
      runAddLayers();
    };

    // Fires for the initial style and for every setStyle - including our own
    // retries and the fallback. Only a real (non-fallback) style counts as the
    // basemap being back.
    const onStyleLoad = () => {
      if (usingOfflineFallback) {
        // Layers live on the style, so the fallback needs them re-added or the
        // fleet disappears along with the basemap.
        sourceReady.current = false;
        runAddLayers();
        return;
      }
      markBasemapLoaded();
      forceEnglishLabels(map);
      // Paint lives on the style, so NIGHT's dimming has to be re-applied to
      // every style that loads - the initial one, a basemap switch, and a
      // recovery retry alike.
      applyImageryBrightness(map, displayModeRef.current);
      imageryErrors = 0;
      sourceReady.current = false;
      runAddLayers();
    };

    /**
     * Imagery tiles failing is a basemap outage; the vector styles' rule cannot
     * see it.
     *
     * `isBasemapStyleFailure` ignores any error carrying a `sourceId`, which was
     * right for the vector styles (#64: one dead tile is not an outage, MapLibre
     * retries it) and leaves imagery completely unalarmed. Imagery is different
     * in the way that matters: its tiles come from OUR api behind auth, so they
     * fail TOGETHER and for reasons that are not transient - a revoked or
     * quota'd key, an expired session, a tightened IP restriction. The backend
     * says so itself: /api/auth/me still reports the switch on, because it can
     * only see that a key is SET.
     *
     * Without this the map keeps its style, keeps `basemapLoaded`, and shows the
     * style's own near-black background with the fleet floating on it - no
     * banner, no Retry, no console line. A silent loss of the ground under the
     * air picture is precisely the failure `docs/operational-standards.md`
     * exists to forbid, and it became the DEFAULT state the day imagery did.
     *
     * Consecutive-count rather than a rate: a burst is one screen of tiles, and
     * `onStyleLoad` zeroes the counter, so a handful of stragglers during a pan
     * cannot accumulate into a false alarm across minutes.
     */
    const onMapError = (e: (maplibregl.ErrorEvent & { sourceId?: string }) | undefined) => {
      if (e?.sourceId === IMAGERY_LAYER_ID) {
        if (++imageryErrors < IMAGERY_ERRORS_BEFORE_OFFLINE) return;
        imageryErrors = 0;
        // `true`: the STYLE loaded fine - it is our own API's document. What
        // failed is everything under it.
        handleBasemapFailure('imagery tiles unavailable', true);
        return;
      }
      if (!isBasemapStyleFailure(e, basemapLoaded)) return;
      handleBasemapFailure(e?.error?.message ?? 'style error');
    };

    /**
     * Whether the camera is off north-up, for the Reset north key (D11).
     *
     * A half-degree deadband on both axes: `easeTo({ bearing: 0, pitch: 0 })`
     * lands on exact zeros, but a touch rotate that the operator has visually
     * straightened can leave a residue of a hundredth of a degree, and a key
     * that reappears for a rotation nobody can see is worse than one that never
     * left. `rotate` and `pitch` fire per frame; the reducer bails out when the
     * boolean has not changed, so a drag on the compass costs one render, not
     * one per frame.
     */
    const syncRotated = () => {
      dispatch({
        type: 'SET_MAP_ROTATED',
        value: Math.abs(map.getBearing()) > 0.5 || map.getPitch() > 0.5,
      });
    };

    map.on('load', onMapLoad);
    map.on('style.load', onStyleLoad);
    map.on('error', onMapError);
    map.on('rotate', syncRotated);
    map.on('pitch', syncRotated);

    // Let the indicator's retry control reach this map instance.
    retryBasemapRef.current = () => {
      if (basemapLoaded) return;
      basemapAttempt = 0;
      loadRealBasemap();
    };

    // A manual style switch loads a different CDN style, which can fail on its
    // own (the two styles are served by different providers, and a network that
    // blocks one may serve the other). Re-arm the watchdog so a failed swap
    // falls back and retries instead of hanging on a half-loaded map.
    rearmBasemapWatchRef.current = () => {
      basemapLoaded = false;
      basemapAttempt = 0;
      usingOfflineFallback = false;
      clearBasemapTimers();
      styleLoadTimer = setTimeout(() => handleBasemapFailure('timeout'), BASEMAP_LOAD_TIMEOUT_MS);
    };

    /** A click the armed mode owns, or the echo of a LOOK click - not the asset's. */
    const clickIsClaimed = () =>
      mapModeClaimsAssetClicks(mapModeRef.current) || Date.now() < lookClickEchoUntilRef.current;

    const handleDroneClick = (e: maplibregl.MapLayerMouseEvent) => {
      // While the ruler is armed a click on the map is a MEASUREMENT, and that
      // includes a click that happens to land on an asset - measuring from an
      // aircraft or a dock is the first thing anyone reaches for. Without this
      // the click did both: it placed the point AND selected the asset, which
      // opens a camera window and re-lays the stage out, moving the ground under
      // the half-finished measurement. Scoped to this one mode rather than to
      // `isMapModeArmed`, deliberately: dock-plan is armed whenever a dock is
      // selected, so a blanket guard here would stop an operator picking a
      // different dock.
      if (clickIsClaimed()) return;
      e.preventDefault();
      const feature = e.features?.[0];
      if (feature?.properties?.id) {
        const droneId = feature.properties.id as string;
        // The one selection that means "show me this aircraft" rather than
        // "select it on the way to something else" — see `DroneSelectSource`.
        onSelectDroneRef.current(droneId, 'map-pick');
        if (!hiddenControllerLinks.current.has(droneId)) {
          dispatch({ type: 'SET_CONTROLLER_LINK', value: droneId });
        }
      }
    };
    map.on('click', 'drone-circles', handleDroneClick);
    map.on('click', 'drone-symbols', handleDroneClick);
    // The bracket renders only around the already-selected drone, so clicking it
    // re-selects the same aircraft - harmless, and it gives the one drone the
    // operator is actually working a much larger hit target than the ~29px icon.
    map.on('click', 'drone-selection-bracket', handleDroneClick);

    const handleDockClick = (e: maplibregl.MapLayerMouseEvent) => {
      if (clickIsClaimed()) return;
      e.preventDefault();
      // The generous 22px dock-hit target can overlap a drone or controller
      // sitting on / launching from the dock. When a more-specific asset marker
      // is under the exact click point it wins (precedence: drone/controller >
      // dock > site) - otherwise one click would open BOTH the dock sheet and
      // the drone telemetry sheet, since MapLibre fires every layer's listener.
      // `trigger-hit` joins this list because a trigger point sits ON its dock:
      // for the only source that exists today the reported position IS the
      // dock's own GPS receiver, so the overlap is the guaranteed geometry
      // rather than an edge case. The detection wins - it is the urgent, and
      // the dismissable, one of the two; the dock is still there afterwards.
      const assetLayers = ['drone-circles', 'drone-symbols', 'controller-circles', 'controller-symbols', 'trigger-hit']
        .filter((id) => map.getLayer(id));
      if (assetLayers.length > 0 && map.queryRenderedFeatures(e.point, { layers: assetLayers }).length > 0) {
        return;
      }
      const feature = e.features?.[0];
      if (feature?.properties?.id) {
        onSelectDockRef.current?.(feature.properties.id as string);
      }
    };
    // Bound to the invisible 'dock-hit' target, never the visible marker, so
    // the click survives any change to how a dock is drawn (T1.2 glyph swap).
    map.on('click', 'dock-hit', handleDockClick);
    // Same reasoning as the drone bracket: it only exists around the selected
    // dock, so this re-selects it rather than clearing it.
    map.on('click', 'dock-selection-bracket', handleDockClick);

    const handleTriggerClick = (e: maplibregl.MapLayerMouseEvent) => {
      // Same guard, same reason, as handleMissionClick below: while a mode is
      // armed the click belongs to that mode. Trigger points sit on docks,
      // which is exactly where routes and zones get drawn - without this, one
      // click both places a vertex and pops a card offering to open the quick
      // dispatch dialog over a half-drawn plan.
      if (isMapModeArmed(mapModeRef.current)) return;
      e.preventDefault();
      const feature = e.features?.[0];
      const id = feature?.properties?.trigger_id;
      if (typeof id !== 'string') return;
      // Only the id is stored - the card resolves against the live list, so it
      // closes by itself when someone else answers this detection.
      if (triggersRef.current.some((t) => t.id === id)) {
        dispatch({ type: 'SET_TRIGGER_CARD', value: { triggerId: id, openedAt: Date.now() } });
      }
    };
    map.on('click', 'trigger-hit', handleTriggerClick);

    const handleMissionClick = (e: maplibregl.MapLayerMouseEvent) => {
      if (!onSelectMissionRef.current) return;
      // A route click is a SELECTION gesture and nothing else. While ANY mode is
      // armed the click means something different — pick a target, drop a
      // waypoint, draw a zone, fly here — and that mode owns it (mode-scoped
      // gestures); handleMapClick below resolves it. One test, not four: a new
      // mode cannot forget to appear here.
      if (isMapModeArmed(mapModeRef.current)) return;
      // Routes pass under aircraft and docks; the more specific asset wins the
      // click, exactly as in handleDockClick above.
      const assetLayers = [
        'drone-circles', 'drone-symbols', 'controller-circles', 'controller-symbols', 'dock-hit',
      ].filter((id) => map.getLayer(id));
      if (assetLayers.length > 0 && map.queryRenderedFeatures(e.point, { layers: assetLayers }).length > 0) {
        return;
      }
      const p = e.features?.[0]?.properties;
      const missionId = (p?.id ?? p?.mission_id) as string | undefined;
      if (missionId) onSelectMissionRef.current(missionId);
    };
    // Bound to the generous invisible hit line, never the visible 2px route —
    // same discipline as dock-hit.
    map.on('click', 'mission-route-hit', handleMissionClick);
    map.on('click', 'mission-waypoint-circles', handleMissionClick);

    const handleCtrlClick = (e: maplibregl.MapLayerMouseEvent) => {
      if (clickIsClaimed()) return;
      e.preventDefault();
      const feature = e.features?.[0];
      if (feature?.properties?.drone_id) {
        const droneId = feature.properties.drone_id as string;
        dispatch({
          type: 'SET_CONTROLLER_LINK',
          value: uiRef.current.controllerLinkId === droneId ? null : droneId,
        });
      }
    };
    map.on('click', 'controller-circles', handleCtrlClick);
    map.on('click', 'controller-symbols', handleCtrlClick);

    const handleSiteCircleClick = (e: maplibregl.MapLayerMouseEvent) => {
      if (clickIsClaimed()) return;
      // A dock's generous hit target can overlap a nearby site marker. When
      // both are under the cursor the dock wins (handleDockClick selects it),
      // so suppress the site popup here instead of opening both at once.
      if (map.getLayer('dock-hit') && map.queryRenderedFeatures(e.point, { layers: ['dock-hit'] }).length > 0) {
        return;
      }
      const feature = e.features?.[0];
      if (!feature?.properties) return;
      const p = feature.properties;
      const coords = (feature.geometry as GeoJSON.Point).coordinates;
      const lat = Number(p.latitude);
      const lng = Number(p.longitude);
      const radius = Number(p.radius_meters);
      const status = p.is_active === 'true' || p.is_active === true;
      const statusDot = status ? 'var(--status-green-marker)' : 'var(--status-gray-marker)';
      const statusLabel = status ? 'ACTIVE' : 'INACTIVE';

      const esc = (s: string) => {
        const d = document.createElement('div');
        d.textContent = s;
        return d.innerHTML;
      };
      new maplibregl.Popup({ closeButton: true, closeOnClick: true, className: 'site-popup', maxWidth: '260px' })
        .setLngLat(coords as [number, number])
        .setHTML(`
          <div style="font-family:var(--font-sans),system-ui,sans-serif;color:var(--foreground);font-size:12px;line-height:1.6">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px">
              <span style="width:8px;height:8px;border-radius:50%;background:${statusDot};display:inline-block"></span>
              <strong style="font-size:13px;letter-spacing:0.03em;text-transform:uppercase">${esc(String(p.name))}</strong>
              <span style="font-size:10px;color:var(--muted-foreground);text-transform:uppercase">${statusLabel}</span>
            </div>
            ${p.address ? `<div style="color:var(--muted-foreground);font-size:11px;margin-bottom:6px">${esc(String(p.address))}</div>` : ''}
            <div style="display:grid;grid-template-columns:auto 1fr;gap:2px 10px;font-size:11px">
              <span style="color:var(--muted-foreground)">LAT</span><span>${lat.toFixed(6)}</span>
              <span style="color:var(--muted-foreground)">LNG</span><span>${lng.toFixed(6)}</span>
              <span style="color:var(--muted-foreground)">RADIUS</span><span>${radius}m</span>
            </div>
          </div>
        `)
        .addTo(map);
    };
    map.on('click', 'site-circles', handleSiteCircleClick);

    /**
     * A click on an NFZ-Israel zone opens its card - but only a click nothing else wants.
     *
     * Map-level rather than layer-scoped, so one card can list every zone under the point (they
     * overlap heavily: a CTR, a helicopter area and an LLP can share a field). The guards are
     * the whole point: the zones cover most of the country, so a layer-scoped listener would
     * fire on nearly every click. Only on an IDLE map (never while placing a point, a waypoint,
     * a zone or a ruler leg - `dock-plan` included, which is armed whenever a dock is selected),
     * and never on anything the picture already answers a click for.
     */
    const NFZ_CLICK_YIELDS_TO = [
      'drone-circles', 'drone-symbols', 'controller-circles', 'controller-symbols', 'dock-hit',
      'trigger-hit', 'mission-route-hit', 'mission-waypoint-circles', 'plan-waypoint-hit',
      'plan-home-circle', 'site-circles', 'site-labels', ...SELECTION_BRACKET_LAYER_IDS,
    ];
    const handleNfzZoneClick = (e: maplibregl.MapMouseEvent) => {
      const ui = uiRef.current;
      if (!ui.showNfzIsrael || clickIsClaimed()) return;
      if (mapModeRef.current.kind !== 'idle') return;
      // A click with something selected is a deselect, and a click with a menu or card open only
      // dismisses it (`handleMapClick`). Neither is a request for zone information.
      if (hasSelectionRef.current) return;
      if (ui.contextMenu || ui.mapDispatchMenu || ui.measureMenu || ui.triggerCard) return;
      const yieldTo = NFZ_CLICK_YIELDS_TO.filter((id) => map.getLayer(id));
      if (yieldTo.length > 0 && map.queryRenderedFeatures(e.point, { layers: yieldTo }).length > 0) return;
      const zoneLayers = NFZ_HIT_LAYER_IDS.filter((id) => map.getLayer(id));
      if (zoneLayers.length === 0) return;
      // A few pixels of slack, so the 4 px points and 2 px lines are not a hunt.
      const slack = 4;
      const zones = uniqueZones(map.queryRenderedFeatures(
        [[e.point.x - slack, e.point.y - slack], [e.point.x + slack, e.point.y + slack]],
        { layers: zoneLayers },
      ));
      if (zones.length === 0) return;
      // DOM content, never setHTML: every string in a zone is untrusted text (D12).
      // `closeOnMove`: a double-click zoom (two clicks, then a zoom) or a pan leaves no card
      // pinned over a picture it no longer describes (slice 2 review, eng 3 / product 2).
      nfzPopupRef.current?.remove();
      nfzPopupRef.current = new maplibregl.Popup({
        closeButton: true, closeOnClick: true, closeOnMove: true, className: 'site-popup', maxWidth: '320px',
      })
        .setLngLat(e.lngLat)
        .setDOMContent(buildNfzZonePopup(zones, ui.nfzSource, nfzCaution(ui.nfzSource, ui.nfzIsraelErr, Date.now())))
        .addTo(map);
    };
    map.on('click', handleNfzZoneClick);

    const handleSiteLabelClick = (e: maplibregl.MapLayerMouseEvent) => {
      // This re-FIRES a map-level click, which every map-level listener sees -
      // including `handleMapClick`, which has already handled the real one. In
      // measure mode that meant a single click on a site label queued TWO
      // measurement points, completing a leg of zero length. Guarding the
      // re-fire rather than the delegate below, because the delegate is exactly
      // what must not run twice.
      if (clickIsClaimed()) return;
      map.fire('click', { ...e, features: e.features, lngLat: e.lngLat, point: e.point, originalEvent: e.originalEvent } as unknown as maplibregl.MapLayerMouseEvent);
    };
    map.on('click', 'site-labels', handleSiteLabelClick);

    const handleSiteCircleEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const handleSiteCircleLeave = () => { setMapInteractionCursor(map, mapModeRef.current); };
    map.on('mouseenter', 'site-circles', handleSiteCircleEnter);
    map.on('mouseleave', 'site-circles', handleSiteCircleLeave);

    /**
     * Is this screen point on something that already belongs to the plan?
     *
     * Clicking an existing plan marker or the dock selects, never adds.
     * Tested against the invisible 'plan-waypoint-hit' target rather than the
     * 8px visible dot: a near-miss must SELECT the waypoint the operator was
     * aiming at, never quietly append a new one to the route being edited.
     *
     * Shared with the double-click handler, which places the first point of an
     * empty route and owes it the same courtesy.
     */
    const pointIsOverPlanFurniture = (point: maplibregl.Point): boolean => {
      const planLayers = ['plan-waypoint-hit', 'plan-home-circle', 'dock-hit']
        .filter((id) => map.getLayer(id));
      return map.queryRenderedFeatures(point, { layers: planLayers }).length > 0;
    };

    /**
     * Place a point into the plan overlay. Shared by every mode that draws a
     * route on the map — the dock launch planner and, from task 3.2, mission
     * compose and edit — because "a click adds a waypoint unless it landed on
     * one" is a property of the overlay, not of which workflow armed it.
     */
    const handlePlanPlacementClick = (e: maplibregl.MapMouseEvent) => {
      if (!onPlanMapClickRef.current) return;
      // Swallow the synthetic click MapLibre fires right after a marker drag.
      if (planDragMovedRef.current) {
        planDragMovedRef.current = false;
        return;
      }
      if (!pointIsOverPlanFurniture(e.point)) {
        planModeClickArbiterRef.current.queueAdd(() => onPlanMapClickRef.current?.(e.lngLat.lat, e.lngLat.lng));
      }
    };

    const handleMapClick = (e: maplibregl.MapMouseEvent) => {
      // A click away from an open measurement menu dismisses it and does
      // nothing else - with the ruler armed it would otherwise also drop a
      // point, and in idle it would also drop the operator's selection.
      if (uiRef.current.measureMenu) {
        dispatch({ type: 'SET_MEASURE_MENU', value: null });
        return;
      }
      // One switch, one meaning. The chain of independent booleans this
      // replaces could not express "exactly one is armed", so the ORDER of the
      // tests was the only thing deciding what a click did.
      //
      // Read into a `const` rather than switched on `mapModeRef.current`
      // directly, so TypeScript narrows across the cases and the `never` in the
      // default below is a real exhaustiveness check. On a mutable ref property
      // it is not: a new `MapMode` variant compiles clean and falls through to
      // `idle`, which is the ONE branch that does something destructive - it
      // clears the operator's drone and dock selection. An armed mode reaching
      // it would show a crosshair, promise a placement in the banner, place
      // nothing, and drop the selection instead.
      const mode = mapModeRef.current;
      switch (mode.kind) {
        case 'goto':
          onGotoPointPickedRef.current?.(e.lngLat.lat, e.lngLat.lng);
          return;
        case 'dispatch-pick':
          onDispatchTargetPickedRef.current?.(e.lngLat.lat, e.lngLat.lng);
          return;
        case 'dock-plan':
        case 'mission-compose':
        case 'mission-edit':
          handlePlanPlacementClick(e);
          return;
        case 'zone-draw':
          onZoneMapClickRef.current?.(e.lngLat.lat, e.lngLat.lng);
          return;
        case 'range-bearing':
          // Parked for the double-click window rather than placed at once, on
          // the same arbiter the plan modes use. Double-clicking to zoom is a
          // reflex on any map, and MapLibre delivers it as two clicks plus its
          // own zoom - so without this the gesture left a COMPLETE zero-length
          // measurement behind (`RNG 0 m`) while the viewport jumped, which
          // reads as the tool breaking rather than as a zoom.
          planModeClickArbiterRef.current.queueAdd(() => {
            onRangeBearingMapClickRef.current?.(e.lngLat.lat, e.lngLat.lng);
          });
          return;
        case 'look-here':
          // Parked like the ruler's click, and for a sharper reason: a
          // double-click to zoom would otherwise send a gimbal command on its
          // first click and drop the selection on its second, which lands on a
          // map the command has already put back to idle.
          lookClickEchoUntilRef.current = Date.now() + LOOK_CLICK_ECHO_MS;
          planModeClickArbiterRef.current.queueAdd(() => {
            onLookHereMapClickRef.current?.(e.lngLat.lat, e.lngLat.lng);
          });
          return;
        case 'idle':
          // The second click of a slow double-click on a LOOK: not a deselect.
          if (Date.now() < lookClickEchoUntilRef.current) return;
          break;
        default: {
          // Not reachable, and that is the point: a variant added to `MapMode`
          // without a case here fails the build rather than silently becoming
          // a click that clears the selection.
          const unhandled: never = mode;
          void unhandled;
          break;
        }
      }
      // 'dock-hit' MUST be included here even though this block only clears
      // DRONE selection: this generic click handler and the dock-hit-scoped
      // handleDockClick both fire for the same click (MapLibre invokes every
      // matching listener). Without dock-hit in this list, clicking a dock
      // made this block think "nothing relevant was clicked" and fire
      // onSelectDrone(''), whose SELECT reducer case unconditionally resets
      // selectedDockId to null too - silently undoing the dock selection
      // handleDockClick had just made, in the same event tick. This was the
      // exact cause of the dock-click regression caught by the T0.3 smoke test.
      // The selection brackets MUST be in this list. They are drawn well outside
      // the marker's own hit area (~23px out, vs a ~14px drone icon), so without
      // them a click on the visible white bracket matches no layer, reads as
      // "clicked empty map", and clears the very selection it is advertising.
      // Mission layers are in this list for the same reason dock-hit is: a
      // route click selects a MISSION (handleMissionClick), and without them
      // this block would read the same click as "empty map" and clear the
      // drone selection in the same tick.
      // The plan overlay is in this list for that same reason. A draft route
      // stays draggable after point entry ends (mode is idle, planEditable is
      // still true), and the synthetic click a browser fires at the end of any
      // drag reaches here — without the plan layers, every successful waypoint
      // drag also silently dropped the operator's drone/dock selection.
      const selectableLayerIds = ['drone-circles', 'drone-symbols', 'controller-circles', 'controller-symbols', 'dock-hit', 'trigger-hit', 'mission-route-hit', 'mission-waypoint-circles', 'plan-waypoint-hit', 'plan-home-circle', ...SELECTION_BRACKET_LAYER_IDS].filter((id) => map.getLayer(id));
      const features = map.queryRenderedFeatures(e.point, { layers: selectableLayerIds });
      if (features.length === 0) {
        onSelectDroneRef.current('');
        hiddenControllerLinks.current.clear();
        dispatch({ type: 'CLEAR_MAP_SELECTION' });
      }
    };
    map.on('click', handleMapClick);

    const handleMapDoubleClick = (e: maplibregl.MapMouseEvent) => {
      // Measuring: the gesture's own clicks are dropped either way. With a line
      // still taking points, the double-click ENDS it and the page puts the
      // ruler down - the same gesture that ends point entry on a route - and
      // the map does not zoom. With no line
      // open there is nothing to end, so the operator gets the zoom they asked
      // for. Returns before the plan branch because the ruler is not a
      // placement mode and owns no overlay to exit.
      //
      // This is exact only while the two clicks fall inside the arbiter's
      // 250 ms window, which is shorter than the ~500 ms an OS calls a
      // double-click. A slower double-click lands its first click as a point
      // before `dblclick` arrives: on an open line that point becomes the line's
      // last one, which is where the operator was pointing; with no line open it
      // starts a one-point line, which finishing drops. Widening the window
      // instead would put a half-second lag on every measurement click.
      // A double-click while LOOK is armed is a zoom and nothing else: the
      // parked click is dropped and MapLibre zooms. LOOK stays armed.
      if (mapModeRef.current.kind === 'look-here') {
        planModeClickArbiterRef.current.cancel();
        return;
      }
      if (mapModeRef.current.kind === 'range-bearing') {
        planModeClickArbiterRef.current.cancel();
        if (rangeBearingDrawingRef.current) {
          e.preventDefault();
          onRangeBearingFinishLineRef.current?.();
        }
        return;
      }
      // Double-click ends point entry for every route-drawing mode, not just
      // the dock planner — same gesture, same overlay. What it means on an
      // EMPTY compose route is a different question, and planDoubleClickAction
      // owns it.
      if (!isPlanPlacementMode(mapModeRef.current) || !onPlanModeExitRef.current) return;
      // Suppress MapLibre's own double-click zoom either way: the gesture is
      // spoken for by the planner while a route is being drawn.
      e.preventDefault();
      // Unconditionally, and FIRST: neither answer wants the clicks of this
      // gesture landing as waypoints afterwards, and how many were still parked
      // is what tells us whether the first of them has already landed.
      const parkedAdds = planModeClickArbiterRef.current.cancel();
      const action = planDoubleClickAction({
        mode: mapModeRef.current,
        waypointCount: planWaypointCountRef.current,
        parkedAdds,
      });
      // Nothing placed yet, so there is nothing to finish: honour the gesture as
      // the single placement it plainly is and leave the mode armed. A
      // double-click that lands on the dock or an existing marker cannot place,
      // so it falls through and means "done" rather than nothing at all.
      if (action === 'place-first' && onPlanMapClickRef.current && !pointIsOverPlanFurniture(e.point)) {
        onPlanMapClickRef.current(e.lngLat.lat, e.lngLat.lng);
        return;
      }
      onPlanModeExitRef.current();
    };
    map.on('dblclick', handleMapDoubleClick);

    // --- Draft mission plan overlay: select on click, reposition on drag. ---
    //
    // Every gesture below binds to 'plan-waypoint-hit' — the invisible
    // PLAN_WAYPOINT_HIT_RADIUS_PX circle — and never to the 8px visible dot,
    // the same discipline as 'dock-hit' and 'mission-route-hit'. Hit-testing
    // the rendered circle meant a mousedown a few pixels off centre matched no
    // layer, so MapLibre's own drag-pan claimed the gesture and the map slid
    // instead of the waypoint. Operators read that as "waypoints can't be
    // dragged, they can only be added".
    let planDragIndex: number | null = null;
    let planWpHovered = false;
    /**
     * Cursor for a pointer that is on — or is dragging — a plan waypoint.
     * Returns false when it is on neither, so callers fall back to the cursor
     * the armed mode asks for.
     */
    const applyPlanWpCursor = (): boolean => {
      // The ruler refuses both the click and the drag on a draft waypoint, so
      // it must not go on advertising a grab. Losing the crosshair here is the
      // exact failure `mapModeWantsCrosshair` exists to prevent: an operator
      // who cannot tell the tool is armed measures from wherever they happened
      // to click. Returning false hands the caller back to
      // `setMapInteractionCursor`, which restores it.
      if (mapModeClaimsAssetClicks(mapModeRef.current)) return false;
      if (planDragIndex !== null) {
        // Mid-drag the pointer routinely outruns the target and fires
        // mouseleave; the grip must not flicker back to a crosshair until the
        // button is actually released.
        map.getCanvas().style.cursor = 'grabbing';
        return true;
      }
      if (!planWpHovered) return false;
      // A waypoint affords a DRAG, so say grab rather than the generic
      // 'pointer' the other interactive layers use. When the route is not
      // editable the drag is not on offer and 'pointer' is the honest answer.
      map.getCanvas().style.cursor =
        canDragPlanWaypoints(planEditableRef.current, onPlanWaypointDragRef.current) ? 'grab' : 'pointer';
      return true;
    };

    const handlePlanWpClick = (e: maplibregl.MapLayerMouseEvent) => {
      // A draft route stays drawn and draggable after point entry ends, so its
      // waypoints are live targets while the ruler is armed - and measuring
      // FROM a planned waypoint is an obvious thing to want. Without this the
      // click both measured and re-selected the waypoint in the plan panel.
      if (clickIsClaimed()) return;
      e.preventDefault();
      const feature = e.features?.[0];
      if (feature?.properties?.kind === 'wp' && onPlanWaypointClickRef.current) {
        onPlanWaypointClickRef.current(Number(feature.properties.index));
      }
    };
    map.on('click', 'plan-waypoint-hit', handlePlanWpClick);

    const handlePlanDragMove = (e: maplibregl.MapMouseEvent) => {
      if (planDragIndex === null) return;
      planDragMovedRef.current = true;
      onPlanWaypointDragRef.current?.(planDragIndex, e.lngLat.lat, e.lngLat.lng);
    };
    const handlePlanDragEnd = () => {
      if (planDragIndex === null) return;
      planDragIndex = null;
      map.off('mousemove', handlePlanDragMove);
      // Dropped, but the pointer may still be over the waypoint — it is still
      // grabbable, so keep saying so instead of reverting to the mode's
      // "click to place" crosshair.
      if (!applyPlanWpCursor()) setMapInteractionCursor(map, mapModeRef.current);
    };
    const handlePlanWpMouseDown = (e: maplibregl.MapLayerMouseEvent) => {
      // The sharper half of the same problem: this one MOVES a planned
      // waypoint. An operator dragging across a draft route to measure along it
      // would have rewritten the route's geometry while believing they were
      // measuring, and nothing on screen would have said so.
      if (clickIsClaimed()) return;
      if (!canDragPlanWaypoints(planEditableRef.current, onPlanWaypointDragRef.current)) return;
      const feature = e.features?.[0];
      if (feature?.properties?.kind !== 'wp') return;
      e.preventDefault(); // keep the map from panning while dragging the marker
      planDragIndex = Number(feature.properties.index);
      planDragMovedRef.current = false;
      applyPlanWpCursor();
      map.on('mousemove', handlePlanDragMove);
      map.once('mouseup', handlePlanDragEnd);
    };
    map.on('mousedown', 'plan-waypoint-hit', handlePlanWpMouseDown);

    const handlePlanWpEnter = () => { planWpHovered = true; applyPlanWpCursor(); };
    const handlePlanWpLeave = () => {
      planWpHovered = false;
      if (!applyPlanWpCursor()) setMapInteractionCursor(map, mapModeRef.current);
    };
    map.on('mouseenter', 'plan-waypoint-hit', handlePlanWpEnter);
    map.on('mouseleave', 'plan-waypoint-hit', handlePlanWpLeave);

    const handleDroneContext = (e: maplibregl.MapLayerMouseEvent) => {
      e.preventDefault();
      const feature = e.features?.[0];
      if (feature?.properties?.id) {
        dispatch({
          type: 'SET_CONTEXT_MENU',
          value: {
            x: e.point.x,
            y: e.point.y,
            droneId: feature.properties.id as string,
            callsign: (feature.properties.callsign as string) || String(feature.properties.id).slice(-6),
          },
        });
      }
    };
    map.on('contextmenu', 'drone-circles', handleDroneContext);
    map.on('contextmenu', 'drone-symbols', handleDroneContext);

    const handleMapContextMenu = (e: maplibregl.MapMouseEvent) => {
      const layers = ['drone-circles', 'drone-symbols'].filter((id) => map.getLayer(id));
      const features = map.queryRenderedFeatures(e.point, { layers });
      if (features.length > 0) return;
      e.preventDefault();
      // A measurement under the cursor gets its own menu (Delete). Queried with
      // a few pixels of slack, because the line is 2 px wide and a right-click
      // that has to land on it exactly is one an operator misses.
      if (onRangeBearingDeleteLineRef.current) {
        const measureLayers = MEASURE_HIT_LAYERS.filter((id) => map.getLayer(id));
        const slack = 6;
        const hit = measureLayers.length > 0
          ? map.queryRenderedFeatures(
            [[e.point.x - slack, e.point.y - slack], [e.point.x + slack, e.point.y + slack]],
            { layers: measureLayers },
          ).find((f) => typeof f.properties?.line === 'number')
          : undefined;
        if (hit) {
          dispatch({
            type: 'SET_MEASURE_MENU',
            value: { x: e.point.x, y: e.point.y, line: hit.properties.line as number },
          });
          return;
        }
      }
      if (onRequestDispatchHereRef.current) {
        dispatch({
          type: 'SET_MAP_DISPATCH_MENU',
          value: { x: e.point.x, y: e.point.y, lat: e.lngLat.lat, lng: e.lngLat.lng },
        });
      } else {
        dispatch({ type: 'SET_MAP_DISPATCH_MENU', value: null });
      }
    };
    map.on('contextmenu', handleMapContextMenu);

    // Pointer over mission routes only when a selection handler exists — a
    // pointer on an inert line advertises a click that does nothing.
    const missionCursorHandlers = ['mission-route-hit', 'mission-waypoint-circles'].map((layer) => {
      const onEnter = () => {
        if (onSelectMissionRef.current) map.getCanvas().style.cursor = 'pointer';
      };
      const onLeave = () => { setMapInteractionCursor(map, mapModeRef.current); };
      map.on('mouseenter', layer, onEnter);
      map.on('mouseleave', layer, onLeave);
      return { layer, onEnter, onLeave };
    });

    map.getCanvas().style.cursor = '';
    // 'plan-waypoint-hit' is deliberately NOT here — it gets grab/grabbing
    // from applyPlanWpCursor above, which this list's flat 'pointer' would
    // overwrite on hover.
    const interactiveLayers = ['drone-circles', 'drone-symbols', 'controller-circles', 'controller-symbols', 'dock-hit', 'trigger-hit', ...SELECTION_BRACKET_LAYER_IDS];
    const layerCursorHandlers = interactiveLayers.map((layer) => {
      const onEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
      const onLeave = () => { setMapInteractionCursor(map, mapModeRef.current); };
      map.on('mouseenter', layer, onEnter);
      map.on('mouseleave', layer, onLeave);
      return { layer, onEnter, onLeave };
    });
    // Hover chip: the first level of the drone interaction — "is this the one
    // I want?" answered without committing to a selection. Only the id and the
    // cursor point are stored; the chip reads live telemetry from the drones
    // prop at render, so it never shows a frozen snapshot of a moving aircraft.
    const droneHoverHandlers = ['drone-circles', 'drone-symbols'].map((layer) => {
      const onMove = (e: maplibregl.MapLayerMouseEvent) => {
        const id = e.features?.[0]?.properties?.id;
        if (typeof id !== 'string') return;
        dispatch({
          type: 'SET_HOVERED_DRONE',
          value: {
            droneId: id,
            x: e.point.x,
            y: e.point.y,
            containerWidth: map.getCanvas().clientWidth,
          },
        });
      };
      const onLeave = () => {
        dispatch({ type: 'SET_HOVERED_DRONE', value: null });
      };
      map.on('mousemove', layer, onMove);
      map.on('mouseleave', layer, onLeave);
      return { layer, onMove, onLeave };
    });

    const handleDragStart = () => {
      map.getCanvas().style.cursor = 'grabbing';
      dispatch({ type: 'CLEAR_FOLLOW' });
    };
    const handleDragEnd = () => { setMapInteractionCursor(map, mapModeRef.current); };
    map.on('dragstart', handleDragStart);
    map.on('dragend', handleDragEnd);

    const handleMouseMove = (e: maplibregl.MapMouseEvent) => {
      dispatch({ type: 'SET_CURSOR_COORDS', value: `${e.lngLat.lat.toFixed(5)}, ${e.lngLat.lng.toFixed(5)}` });
    };
    const handleMouseOut = () => { dispatch({ type: 'SET_CURSOR_COORDS', value: null }); };
    map.on('mousemove', handleMouseMove);
    map.on('mouseout', handleMouseOut);

    const onAirspaceMoveEnd = () => {
      if (!uiRef.current.showAirspace) return;
      if (airspaceMoveTimer) clearTimeout(airspaceMoveTimer);
      airspaceMoveTimer = setTimeout(() => {
        fetchAirspaceOnMove();
      }, 650);
    };
    map.on('moveend', onAirspaceMoveEnd);

    mapRef.current = map;
    setMapInstance(map);
    // E2E-only hook so Playwright can locate real map features (docks/drones)
    // by querying rendered layers and projecting lng/lat to exact screen
    // pixels, instead of guessing coordinates (fragile - exactly the class
    // of test that would have caught today's dock-click regression).
    // NODE_ENV check is evaluated at build time, so this branch is stripped
    // entirely from production bundles.
    if (process.env.NODE_ENV !== 'production') {
      (window as unknown as { __shovronMapForE2E?: maplibregl.Map }).__shovronMapForE2E = map;
    }

    return () => {
      clearBasemapTimers();
      retryBasemapRef.current = null;
      rearmBasemapWatchRef.current = null;
      map.off('load', onMapLoad);
      map.off('style.load', onStyleLoad);
      map.off('error', onMapError);
      map.off('rotate', syncRotated);
      map.off('pitch', syncRotated);
      map.off('moveend', onAirspaceMoveEnd);
      map.off('click', 'drone-circles', handleDroneClick);
      map.off('click', 'drone-symbols', handleDroneClick);
      map.off('click', 'dock-hit', handleDockClick);
      map.off('click', 'trigger-hit', handleTriggerClick);
      map.off('click', 'controller-circles', handleCtrlClick);
      map.off('click', 'controller-symbols', handleCtrlClick);
      map.off('click', 'mission-route-hit', handleMissionClick);
      map.off('click', 'mission-waypoint-circles', handleMissionClick);
      for (const { layer, onEnter, onLeave } of missionCursorHandlers) {
        map.off('mouseenter', layer, onEnter);
        map.off('mouseleave', layer, onLeave);
      }
      map.off('click', 'site-circles', handleSiteCircleClick);
      map.off('click', handleNfzZoneClick);
      map.off('click', 'site-labels', handleSiteLabelClick);
      map.off('mouseenter', 'site-circles', handleSiteCircleEnter);
      map.off('mouseleave', 'site-circles', handleSiteCircleLeave);
      map.off('click', handleMapClick);
      map.off('dblclick', handleMapDoubleClick);
      map.off('click', 'plan-waypoint-hit', handlePlanWpClick);
      map.off('mousedown', 'plan-waypoint-hit', handlePlanWpMouseDown);
      map.off('mouseenter', 'plan-waypoint-hit', handlePlanWpEnter);
      map.off('mouseleave', 'plan-waypoint-hit', handlePlanWpLeave);
      map.off('mousemove', handlePlanDragMove);
      map.off('mouseup', handlePlanDragEnd);
      map.off('contextmenu', 'drone-circles', handleDroneContext);
      map.off('contextmenu', 'drone-symbols', handleDroneContext);
      map.off('contextmenu', handleMapContextMenu);
      for (const { layer, onEnter, onLeave } of layerCursorHandlers) {
        map.off('mouseenter', layer, onEnter);
        map.off('mouseleave', layer, onLeave);
      }
      for (const { layer, onMove, onLeave } of droneHoverHandlers) {
        map.off('mousemove', layer, onMove);
        map.off('mouseleave', layer, onLeave);
      }
      map.off('dragstart', handleDragStart);
      map.off('dragend', handleDragEnd);
      map.off('mousemove', handleMouseMove);
      map.off('mouseout', handleMouseOut);
      if (airspaceMoveTimer) clearTimeout(airspaceMoveTimer);
      planModeClickArbiterRef.current.cancel();
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
      setMapInstance(null);
      sourceReady.current = false;
      if (process.env.NODE_ENV !== 'production') {
        delete (window as unknown as { __shovronMapForE2E?: maplibregl.Map }).__shovronMapForE2E;
      }
    };
  }, [hiddenControllerLinks]);

  useEffect(() => {
    setMapInteractionCursor(mapRef.current, mapMode);
  }, [mapMode]);

  // Leaving point entry drops any click the arbiter was still holding. Every
  // route-drawing mode shares that arbiter, and the measurement tool does too,
  // so the test is which modes park clicks - not the dock planner specifically.
  //
  // Keyed on the MODE, not on `planPlacementActive` alone. That flag is false
  // both before and after a `range-bearing -> idle` transition, so the effect
  // never re-ran on the one transition that needed it: a click parked and then
  // Escape pressed inside the 250 ms window placed a point on a map that was no
  // longer armed - and since the measurement now outlives the mode, it stayed
  // there.
  useEffect(() => {
    const parksClicks = planPlacementActive || mapModeParksClicks(mapMode);
    if (!parksClicks) planModeClickArbiterRef.current.cancel();
  }, [mapMode, planPlacementActive, planModeClickArbiterRef]);

  useEffect(() => {
    appendLiveTrailPoints(drones, ui.trailDroneIds, trailHistory.current);
    appendAutoTrailPoints(drones, autoTrailHistory.current);
    applySourcesToMap();
  }, [drones, ui.trailDroneIds, ui.controllerLinkId, ui.stalenessTick, ui.showRangeRings, ui.assetVisibility, applySourcesToMap, trailHistory, autoTrailHistory]);

  useEffect(() => {
    if (hasFlewToSite.current || sites.length === 0 || !mapRef.current) return;
    hasFlewToSite.current = true;
    const s = sites[0];
    mapRef.current.flyTo({
      center: [s.longitude, s.latitude],
      offset: cameraOffsetRef.current,
      zoom: 14,
      duration: 1500,
    });
  }, [sites]);

  // Keep the camera locked on the followed drone as new telemetry arrives.
  // Reads followDroneId non-reactively; the live `drones` stream is the sync trigger.
  const trackFollowedDrone = useEffectEvent(() => {
    const followId = ui.followDroneId;
    if (!followId || !mapRef.current) return;
    const drone = drones.find((d) => d.drone_id === followId);
    if (drone) {
      mapRef.current.easeTo({
        center: [drone.longitude, drone.latitude],
        offset: cameraOffsetRef.current,
        duration: 300,
      });
    }
  });
  useEffect(() => { trackFollowedDrone(); }, [drones]);

  // Sync the pulsing rings on stale drones with the MapLibre marker layer.
  // Reads live `drones` non-reactively; triggered by telemetry + a 1s timer.
  const syncStalePulseMarkers = useEffectEvent(() => {
    const map = mapRef.current;
    if (!map) return;
    const now = Date.now();
    // Gated on the asset filter, which the substitution in `buildSourceUpdates`
    // cannot reach from here - these are imperative markers, not a source. See
    // `stalePulseIds`, which owns the rule and is where it is tested. An empty
    // set makes the removal loop below tear every ring down.
    // `compact` is exempt from the filter for the sources above, and must be
    // for the rings too, or the control minimap keeps the aircraft and loses
    // its link-degradation cue.
    const pulseIds = stalePulseIds(drones, now, compact || uiRef.current.assetVisibility.drones);
    for (const [id, marker] of stalePulseMarkersRef.current) {
      if (!pulseIds.has(id)) {
        marker.remove();
        stalePulseMarkersRef.current.delete(id);
      }
    }
    for (const d of drones) {
      if (!pulseIds.has(d.drone_id)) continue;
      let marker = stalePulseMarkersRef.current.get(d.drone_id);
      if (!marker) {
        const el = document.createElement('div');
        el.className = 'stale-drone-pulse-ring';
        el.setAttribute('aria-hidden', 'true');
        marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([d.longitude, d.latitude])
          .addTo(map);
        stalePulseMarkersRef.current.set(d.drone_id, marker);
      } else {
        marker.setLngLat([d.longitude, d.latitude]);
      }
    }
  });
  // Re-sync on each telemetry update.
  useEffect(() => { syncStalePulseMarkers(); }, [drones, ui.assetVisibility, compact]);
  // Re-sync once per second so drones that go silent begin pulsing from elapsed time.
  useEffect(() => {
    const interval = window.setInterval(syncStalePulseMarkers, 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const markers = stalePulseMarkersRef.current;
    return () => {
      markers.forEach((m) => m.remove());
      markers.clear();
    };
  }, [stalePulseMarkersRef]);

  // Remove the "you are here" marker when the map unmounts.
  useEffect(() => {
    const ref = myLocationMarkerRef;
    return () => {
      ref.current?.remove();
      ref.current = null;
    };
  }, []);

  // Animate the active mission leg only while a mission is executing
  // (planActiveIndex is a live waypoint index). Idle otherwise - no wasted RAF.
  useEffect(() => {
    const map = mapRef.current;
    if (map === null || planActiveIndex === null) return;
    const stop = startPlanActiveLegAnimation(map);
    return stop;
  }, [planActiveIndex]);

  // Ease to a drone when selection changes (incl. selection driven from other
  // panels). External selection prop is the sync trigger; guarded to fire once.
  const easeToSelectedDrone = useEffectEvent(() => {
    if (!mapRef.current) return;
    const drone = drones.find((d) => d.drone_id === selectedDroneId);
    if (drone) mapRef.current.easeTo({
      center: [drone.longitude, drone.latitude],
      offset: cameraOffsetRef.current,
      duration: 500,
    });
  });
  useEffect(() => {
    if (!selectedDroneId) {
      prevSelectedRef.current = null;
      return;
    }
    if (prevSelectedRef.current === selectedDroneId) return;
    prevSelectedRef.current = selectedDroneId;
    easeToSelectedDrone();
  }, [selectedDroneId]);

  useEffect(() => {
    const handler = () => dispatch({ type: 'SET_FULLSCREEN', value: !!document.fullscreenElement });
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const { lat, lng } = (e as CustomEvent).detail;
      mapRef.current?.flyTo(panCameraOptions(lng, lat, cameraOffsetRef.current, 1200));
    };
    window.addEventListener('flyToSite', handler);
    return () => window.removeEventListener('flyToSite', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const { lat, lng } = (e as CustomEvent).detail;
      mapRef.current?.flyTo(panCameraOptions(lng, lat, cameraOffsetRef.current, 800));
    };
    window.addEventListener('flyToDrone', handler);
    return () => window.removeEventListener('flyToDrone', handler);
  }, []);

  // -------------------------------------------------------------------------
  // Manual flight owns the camera while an operator holds authority.
  //
  // The control surface (ManualMapControl) draws its grab halo ON the aircraft
  // symbol, which is only honest while the aircraft is held under the surface's
  // anchor, and its outer ring only means "four seconds of travel" at the zoom
  // manual-vector.ts calibrates. So manual mode asks for all three: the zoom
  // once on entry, the anchor offset with it, then a centre-only track on every
  // telemetry tick.
  //
  // The offset is what lets the control leave the middle of the screen. On a
  // touch pointer the surface anchors itself in a bottom corner (a tablet is
  // held two-handed, so the centre is the least reachable place on it) and the
  // aircraft has to follow it there; on a mouse the offset is [0, 0] and this
  // behaves exactly as it always did.
  //
  // Zoom is applied ONCE rather than re-asserted, deliberately. Re-applying it
  // per tick would snap the camera back every time the operator tried to look
  // further out, and the gesture does not need it: the vector is normalised to
  // the ring, not to map distance, so a zoomed-out map still flies correctly —
  // it just shows the ring covering more ground than the label says.
  //
  // Driven by a window event, the channel this map already exposes for outside
  // camera control (flyToSite / flyToDrone above), rather than a prop threaded
  // through the whole COP for a mode that is off almost all the time.
  // -------------------------------------------------------------------------
  const applyManualFocus = useEffectEvent(
    (
      droneId: string | null,
      zoom: number | null,
      offset: [number, number],
      sessionActive: boolean,
    ) => {
      manualFocusIdRef.current = droneId;
      manualFocusZoomRef.current = zoom;
      manualFocusOffsetRef.current = offset;
      manualSessionActiveRef.current = sessionActive;
      // Keyed to the SESSION, deliberately, and not to `droneId` — `droneId` is
      // only "follow this aircraft", which the operator drops every time they
      // pan to look at something. Rotation and pitch are the operator's again
      // when the session ends and not one moment sooner. Runs before the lookup
      // below so it also holds when the aircraft is not in `drones` yet.
      setManualRotationLock(mapRef.current, sessionActive);
      if (!droneId || !mapRef.current) return;
      const drone = drones.find((d) => d.drone_id === droneId);
      if (!drone) return;
      mapRef.current.easeTo({
        center: [drone.longitude, drone.latitude],
        ...(zoom === null ? {} : { zoom }),
        // Where the aircraft ends up relative to the container centre. The
        // surface computed it from its own measured box, which is this same
        // container, so the halo lands exactly on the symbol.
        offset,
        // North-up, always. The control surface converts a drag into a compass
        // bearing on the assumption that screen-up is north; a map left rotated
        // from some earlier look would silently fly the aircraft off-bearing by
        // exactly that rotation. `setManualRotationLock` above is what keeps it
        // there — this is the animation, not the guarantee.
        bearing: 0,
        pitch: 0,
        duration: 400,
      });
    },
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const { droneId, sessionActive, zoom, offset } = readManualFocusDetail(
        (e as CustomEvent).detail,
      );
      // Same drone AND the same calibration AND the same anchor: a re-render of
      // the control surface, not a new session. Re-easing there would fight the
      // operator's own pan every time the surface re-measured.
      //
      // The zoom half of that comparison is what makes the stage layouts work:
      // re-hosting the surface in the 280 px minimap gives the ring a smaller
      // radius, so it means 20 m of ground only at a NEW zoom — and skipping
      // that would leave a ring labelled "four seconds" covering twice that.
      // The offset half is what makes a handedness switch take effect: the ring
      // moves to the other corner, and the aircraft has to move with it.
      // Both are compared with a tolerance because the calibration tracks
      // latitude, which moves in the last decimal on every telemetry frame.
      if (
        droneId !== null
        && droneId === manualFocusIdRef.current
        && sessionActive === manualSessionActiveRef.current
        && sameManualCalibration(zoom, manualFocusZoomRef.current)
        && sameManualOffset(offset, manualFocusOffsetRef.current)
      ) {
        return;
      }
      applyManualFocus(droneId, zoom, offset, sessionActive);
    };
    window.addEventListener('manualFocus', handler);
    return () => window.removeEventListener('manualFocus', handler);
  }, []);

  const trackManualFocus = useEffectEvent(() => {
    const droneId = manualFocusIdRef.current;
    if (!droneId || !mapRef.current) return;
    const drone = drones.find((d) => d.drone_id === droneId);
    if (drone) {
      // The same offset the focus was applied with, or the aircraft would walk
      // back to the middle of the screen on the next telemetry frame and leave
      // the grab halo over empty map.
      mapRef.current.easeTo({
        center: [drone.longitude, drone.latitude],
        offset: manualFocusOffsetRef.current,
        duration: 300,
      });
    }
  });
  useEffect(() => { trackManualFocus(); }, [drones]);

  // Picking a mission pans to the middle of its route and leaves the zoom
  // alone, the same rule as picking a dock or an aircraft. It used to
  // `fitBounds` the route, which rescaled the picture on every pick and threw
  // away the view the operator picked it FROM.
  useEffect(() => {
    const handler = (e: Event) => {
      const { lat, lng } = (e as CustomEvent).detail;
      mapRef.current?.flyTo(panCameraOptions(lng, lat, cameraOffsetRef.current, 800));
    };
    window.addEventListener('panToMission', handler);
    return () => window.removeEventListener('panToMission', handler);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => dispatch({ type: 'TICK_STALENESS', nowMs: Date.now() }), 1000);
    return () => window.clearInterval(id);
  }, []);

  /**
   * A drone the operator asked for rings on, waiting for its selection to land.
   *
   * One shot, cleared on the next selection change whichever way it goes. If the
   * confirm dialog is cancelled the selection never changes, so this lingers
   * until some later selection clears it; bounded and silent, where the
   * alternative was rings drawn around an aircraft nobody asked about.
   */
  const pendingRingsRef = useRef<string | null>(null);

  useEffect(() => {
    const pending = pendingRingsRef.current;
    if (!pending) return;
    pendingRingsRef.current = null;
    if (selectedDroneId !== pending) return;
    if (!uiRef.current.showRangeRings) dispatch({ type: 'TOGGLE_RANGE_RINGS' });
  }, [selectedDroneId]);

  const handleContextAction = (action: string) => {
    if (!ui.contextMenu) return;
    const { droneId } = ui.contextMenu;
    switch (action) {
      case 'trail': {
        const enabling = !ui.trailDroneIds.has(droneId);
        enableTrailForDrone(droneId, enabling);
        break;
      }
      case 'center': {
        const drone = drones.find((d) => d.drone_id === droneId);
        if (drone) {
          mapRef.current?.flyTo(
            panCameraOptions(drone.longitude, drone.latitude, cameraOffsetRef.current, 800),
          );
        }
        break;
      }
      case 'controller':
        if (ui.controllerLinkId === droneId) {
          dispatch({ type: 'SET_CONTROLLER_LINK', value: null });
          hiddenControllerLinks.current.add(droneId);
        } else {
          dispatch({ type: 'SET_CONTROLLER_LINK', value: droneId });
          hiddenControllerLinks.current.delete(droneId);
          applySourcesToMap();
        }
        break;
      case 'video':
        // `onOpenVideo` selects this aircraft itself. Selecting here as well
        // dispatched the same SELECT twice, and under held manual authority the
        // COP parks each one behind the exit confirm — so the second overwrote
        // the first and one of the two `after` hooks was silently dropped.
        onOpenVideo?.(droneId);
        break;
      case 'rangeRings': {
        // `showRangeRings` is one flag for the whole map, but the rings are only
        // ever drawn around the SELECTED airborne aircraft (`rangeRingAnchor`).
        // This menu opens on any aircraft, so turning them on from here has to
        // pick that aircraft too - otherwise the operator asks for rings on the
        // one under the cursor and gets them around a different one, which on a
        // tactical picture is worse than getting nothing.
        if (selectedDroneId === droneId) {
          dispatch({ type: 'TOGGLE_RANGE_RINGS' });
        } else {
          // Ask for the selection and let the flag FOLLOW it. Selecting is not
          // guaranteed: while manual authority is held the COP parks the request
          // behind a confirm dialog, and flipping the flag here meant a cancelled
          // dialog left rings drawn around the hand-flown aircraft - the exact
          // wrong-airframe failure this case exists to prevent.
          pendingRingsRef.current = droneId;
          onSelectDroneRef.current(droneId);
        }
        break;
      }
      case 'follow': {
        const willFollow = ui.followDroneId !== droneId;
        dispatch({ type: 'TOGGLE_FOLLOW', droneId });
        if (willFollow) {
          const drone = drones.find((d) => d.drone_id === droneId);
          if (drone) {
            mapRef.current?.easeTo({
              center: [drone.longitude, drone.latitude],
              offset: cameraOffsetRef.current,
              duration: 300,
            });
          }
        }
        break;
      }
    }
    dispatch({ type: 'CLOSE_MENUS' });
  };

  // Re-read the staleness tick so the component re-renders on each tick; the map
  // layer effect (keyed on ui.stalenessTick) is what recomputes per-drone staleness.
  void ui.stalenessTick;

  /**
   * What the range-rings item on the context menu should say, for the aircraft
   * the menu was opened on.
   *
   * Not memoised: `ui.contextMenu` is null for all but a moment, so this is an
   * early `null` on essentially every render, and while the menu IS open the
   * answer has to follow the staleness clock - an aircraft whose link goes LOST
   * under an open menu stops being a legal anchor. That clock is
   * `ui.stalenessNowMs` and not `Date.now()`, because this runs during render.
   */
  const contextRangeRings = ui.contextMenu
    ? {
        shown: ui.showRangeRings && selectedDroneId === ui.contextMenu.droneId,
        drawable: rangeRingAnchor(drones, ui.contextMenu.droneId, ui.stalenessNowMs) !== null,
      }
    : null;
  const hasTrail = ui.contextMenu ? ui.trailDroneIds.has(ui.contextMenu.droneId) : false;
  const hasCtrlLink = ui.contextMenu ? ui.controllerLinkId === ui.contextMenu.droneId : false;
  const restrictionsActive = ui.tfrItems.length > 0 || !ui.notamClearToFly;

  const controlsView = useMemo(
    () => ({
      airspace: ui.showAirspace,
      nfzIsrael: ui.showNfzIsrael,
      missions: ui.showMissions,
      altitudeLabels: ui.showAltitudeLabels,
      // Read by the map key only. The rings' own key moved to the aircraft's
      // right-click menu (amendment B); the legend still has to say whether they
      // are on the map.
      rangeRings: ui.showRangeRings,
    }),
    [ui.showAirspace, ui.showNfzIsrael, ui.showMissions, ui.showAltitudeLabels, ui.showRangeRings],
  );

  /**
   * What the map KEY is allowed to explain, which is what the map is drawing.
   *
   * `MapLegend`'s own rule is that a group renders only while its symbols are
   * on the map - it already drops the RC row when no controller is reporting.
   * The asset filter has to obey that rule rather than sit beside it, or the
   * key ends up decoding a dock glyph on a map with no docks, which is exactly
   * the kind of quiet lie a tactical display must not tell. Filtered here, so
   * `MapLegend` itself needs to know nothing about the filter.
   */
  const legendInputs = useMemo(
    () => ({
      controllers: ui.assetVisibility.controllers ? controllers : EMPTY_CONTROLLERS,
      cooperativeTraffic: ui.assetVisibility.traffic ? cooperativeTraffic : EMPTY_COOPERATIVE,
      sites: ui.assetVisibility.sites ? sites : EMPTY_SITES,
      dockCount: ui.assetVisibility.docks ? docks.length : 0,
      // A trail belongs to an aircraft. With the aircraft off the map the line
      // is off it too (`buildSourceUpdates` empties the source), so the key
      // must not go on offering to decode it.
      trailCount: ui.assetVisibility.drones ? ui.trailDroneIds.size : 0,
    }),
    [ui.assetVisibility, ui.trailDroneIds, controllers, cooperativeTraffic, sites, docks],
  );

  /**
   * The open trigger card, resolved against the LIVE list rather than the
   * snapshot the click took.
   *
   * This is what closes the card when another operator answers the same
   * detection: `trigger_resolved` removes the row, `triggers` no longer
   * contains the id, and the card that was still offering "Dispatch drone
   * here" for an aircraft already on its way simply stops rendering.
   */
  const openTrigger = ui.triggerCard
    ? triggers.find((t) => t.id === ui.triggerCard!.triggerId) ?? null
    : null;

  const droneOverlayNode = selectedDroneId ? selectedDroneOverlay : null;
  /** Stable across renders, unlike the node — see the ResizeObserver below. */
  const hasDroneOverlay = Boolean(droneOverlayNode);

  // FOLLOW and TRACK for the selected aircraft, handed to the control cluster
  // rather than to the overlay. The map already owns `followDroneId` and
  // `trailDroneIds`, so building this here keeps the toggles reading real state
  // instead of a copy — the same reason the overlay used to receive them.
  const selectedDroneControls = useMemo<SelectedDroneMapControls | null>(() => {
    if (!selectedDroneId) return null;
    const drone = drones.find((d) => d.drone_id === selectedDroneId);
    if (!drone) return null;
    return {
      callsign: drone.callsign?.trim() || drone.drone_id.slice(-6).toUpperCase(),
      isFollowing: ui.followDroneId === selectedDroneId,
      isTrailShown: ui.trailDroneIds.has(selectedDroneId),
      onToggleFollow: () => dispatch({ type: 'TOGGLE_FOLLOW', droneId: selectedDroneId }),
      onToggleTrail: () => enableTrailForDrone(selectedDroneId, !ui.trailDroneIds.has(selectedDroneId)),
      // The one selection-scoped key whose state the map does NOT own: the
      // stage beside this map is the COP's, so the press is handed straight up.
      videoBeside,
      lookHere,
    };
  }, [selectedDroneId, drones, ui.followDroneId, ui.trailDroneIds, dispatch, enableTrailForDrone, videoBeside, lookHere]);

  // The selected drone's card is glued to its marker imperatively rather than
  // through React state. The map fires `move` at animation frame rate — while
  // following a drone it eases on every telemetry tick — and routing that
  // through a reducer would re-render the whole map shell 60 times a second for
  // a position that is pure presentation.
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const positionOverlay = useEffectEvent(() => {
    const map = mapRef.current;
    const el = overlayRef.current;
    if (!map || !el) return;
    const drone = selectedDroneId
      ? drones.find((d) => d.drone_id === selectedDroneId)
      : undefined;
    if (!drone) return;

    const point = map.project([drone.longitude, drone.latitude]);
    const canvas = map.getCanvas();
    const { left, top } = anchoredCardPosition({
      point: { x: point.x, y: point.y },
      card: { width: el.offsetWidth, height: el.offsetHeight },
      container: { width: canvas.clientWidth, height: canvas.clientHeight },
      // The canvas is bigger than the visible map under the tactical shell, and
      // an aircraft flying low on the picture is exactly the one whose chip would
      // otherwise be clamped to the canvas bottom and land under the command bar.
      // Its callsign and the two numbers that decide whether the sortie continues
      // are the last things that should need a pan to read.
      insets: chromeInsets,
    });
    el.style.transform = `translate(${left}px, ${top}px)`;
    el.style.visibility = 'visible';
  });

  // Layout effect, not effect: position before paint so the card never flashes
  // at the top-left corner on the frame it appears.
  useLayoutEffect(() => {
    positionOverlay();
    // positionOverlay is a useEffectEvent — it gets a new identity every render,
    // so listing it would re-run this on every telemetry tick. It always sees
    // fresh props by design.
    //
    // `chromeInsets` is in the list because a bar appearing changes where the
    // chip may sit without changing the selection or the telemetry, and the chip
    // would otherwise stay under it until the next position tick.
  }, [droneOverlayNode, selectedDroneId, drones, chromeInsets]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onMove = () => positionOverlay();
    map.on('move', onMove);
    map.on('resize', onMove);
    return () => {
      map.off('move', onMove);
      map.off('resize', onMove);
    };
    // Deliberately empty: re-subscribing the MapLibre listeners on every render
    // would churn the hottest path in the app. positionOverlay is an effect
    // event and mapRef is stable.
  }, []);

  /*
    The overlay also has to be re-anchored when it resizes ITSELF, which nothing
    above notices.

    `anchoredCardPosition` clamps against the container using the overlay's
    MEASURED box, so a stale measurement means a stale clamp. DroneSelectionChip
    changes its own height off its own 1 Hz ticker, with no prop change and no
    map event: at the stale threshold it grows the `· 47s` span, and when a
    condition opens it grows a whole second row. The effect above cannot see
    either — `drones` only changes when telemetry ARRIVES, and the case that
    grows the chip is telemetry that has STOPPED. With the fleet quiet and the
    map idle, the chip near a container edge stays clamped for its old, smaller
    box and the new exception pill is cut off by the map root's `overflow-hidden`
    — on precisely the aircraft that just went silent.

    This was masked until the selected-drone overlay became a plain node: as a
    render function it was re-invoked inside this component, so the 1 Hz
    TICK_STALENESS re-render produced a new element identity and re-fired the
    layout effect every second by accident. A ResizeObserver states the
    dependency instead of relying on that.
  */
  useEffect(() => {
    const el = overlayRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => positionOverlay());
    observer.observe(el);
    return () => observer.disconnect();
    // Keyed on WHETHER the overlay is mounted, not on the node: the node is a
    // fresh element object on every CopView render, which would tear down and
    // rebuild the observer at telemetry rate for an element that never changed.
    // The wrapper div mounts and unmounts with this boolean, and it is the
    // wrapper the ref points at.
    //
    // This cannot feed itself: positionOverlay only writes `transform` and
    // `visibility`, neither of which changes the observed border box.
  }, [hasDroneOverlay]);

  /*
    The trigger card gets the same treatment as the drone overlay, for the same
    reasons: projected from the trigger's own lng/lat so it tracks its point
    across a pan, and clamped by `anchoredCardPosition` so it can never be cut
    off by the map root's `overflow-hidden`. The card carries Dismiss and Close
    as its last two rows, so a card clipped at the bottom edge is one an
    operator cannot dismiss at all.
  */
  const triggerCardRef = useRef<HTMLDivElement | null>(null);
  const positionTriggerCard = useEffectEvent(() => {
    const map = mapRef.current;
    const el = triggerCardRef.current;
    if (!map || !el || !openTrigger) return;

    const point = map.project([openTrigger.longitude, openTrigger.latitude]);
    const canvas = map.getCanvas();
    const { left, top } = anchoredCardPosition({
      point: { x: point.x, y: point.y },
      card: { width: el.offsetWidth, height: el.offsetHeight },
      container: { width: canvas.clientWidth, height: canvas.clientHeight },
      // Same reason the chip above passes them: the side panel floats over the
      // canvas rather than narrowing it, so a trigger point near the right edge
      // would put its card under the panel. Dismiss and Close are its last two
      // rows, so that is a card an operator can neither act on nor get rid of.
      insets: chromeInsets,
    });
    el.style.transform = `translate(${left}px, ${top}px)`;
    el.style.visibility = 'visible';
  });

  useLayoutEffect(() => {
    positionTriggerCard();
    // `chromeInsets` for the same reason it is in the overlay's list: the panel
    // opening changes where the card may sit without changing the trigger.
  }, [openTrigger, chromeInsets]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onMove = () => positionTriggerCard();
    map.on('move', onMove);
    map.on('resize', onMove);
    return () => {
      map.off('move', onMove);
      map.off('resize', onMove);
    };
  }, []);

  /**
   * RE-CLAMP WHEN THE CARD ITSELF GROWS.
   *
   * `positionTriggerCard` clamps against a height it measures at the moment it runs, and the two
   * effects above only run when the trigger, the chrome or the map moves. The card's own height
   * changes for reasons none of those observe: the "why not the other aircraft" list opening, and
   * a proposal arriving into a card that is already open. Unobserved, a taller card runs past the
   * map root's `overflow-hidden` and loses its bottom rows - which is the exact failure the clamp
   * exists to prevent ("a card clipped at the bottom edge is one an operator cannot dismiss").
   *
   * An observer rather than a callback prop, because `positionTriggerCard` is a `useEffectEvent`
   * and cannot be passed out of this component.
   */
  useEffect(() => {
    const el = triggerCardRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => positionTriggerCard());
    observer.observe(el);
    return () => observer.disconnect();
  }, [openTrigger]);

  // Resolved here rather than in the component body so the chip always reads
  // live telemetry (the reducer stores only an id), and suppressed for the
  // selected drone, whose card already shows everything the chip would.
  const hoverChip = useMemo(() => {
    const hovered = ui.hoveredDrone;
    if (!hovered || hovered.droneId === selectedDroneId) return null;
    const drone = drones.find((d) => d.drone_id === hovered.droneId);
    return drone ? { drone, x: hovered.x, y: hovered.y, containerWidth: hovered.containerWidth } : null;
  }, [ui.hoveredDrone, drones, selectedDroneId]);

  /** Which handover line colours the map key has to explain right now (see `MapLegend`). */
  const handoverKey = useMemo(() => ({
    live: handoverLinks.some((l) => l.attention === 'none'),
    attention: handoverLinks.some((l) => l.attention === 'attention'),
  }), [handoverLinks]);

  return {
    handoverKey,
    wrapperRef,
    containerRef,
    mapInstance,
    ui,
    hydrated,
    dispatch,
    hoverChip,
    chromeInsets,
    overlayRef,
    droneOverlayNode,
    selectedDroneControls,
    restrictionsActive,
    hasTrail,
    hasCtrlLink,
    contextRangeRings,
    onOpenZones,
    rangeBearing,
    controlsView,
    legendInputs,
    assetsHeld,
    handleToggleMapAsset,
    resetNorth,
    goToMyLocation,
    selectStyle,
    satelliteAvailable,
    toggleFullscreen,
    retryBasemap,
    handleToggleAirspace,
    handleToggleNfzIsrael,
    retryNfzIsrael,
    handleToggleMissions,
    handleToggleAltitudeLabels,
    handleContextAction,
    drones,
    missions,
    controllers,
    cooperativeTraffic,
    sites,
    docks,
    onRequestDispatchHere,
    onDispatchFromTrigger,
    onDismissTrigger,
    onApproveTrigger,
    onRejectTrigger,
    openTrigger,
    triggerCardRef,
    triggers,
    handoverLinks,
    onOpenVideo,
  };
}

export function DroneMap(props: DroneMapProps) {
  const c = useDroneMapController(props);
  const controlsMapStyle = c.hydrated ? c.ui.mapStyle : 'dark';

  return (
    <div ref={c.wrapperRef} className="relative w-full h-full rounded-lg overflow-hidden border border-border bg-card">
      <div ref={c.containerRef} className="w-full h-full" />

      {/* Google's Map Tiles terms require their attribution wherever the imagery
          is shown, and the map is created with `attributionControl: false`, so
          MapLibre will not render the source's own attribution string. This is
          that obligation, met explicitly and only while the imagery is on
          screen. Bottom-left, out of the way of the control column on the right
          and the readouts along the bottom-centre; pointer-events-none so it can
          never intercept a click meant for the map underneath. Not a decoration
          and not optional - removing it puts the deployment out of licence.

          The shadow is `black`, not a token and not an rgba(): this sits on
          Google's imagery, which is outside the theme entirely - one pan crosses
          dark vegetation and near-white concrete, so no surface token can be
          correct behind it. That is the mode-free scrim case the colour-literal
          gate leaves ungated on purpose. It was an rgba() first, which is the
          same rule broken by another door, and CI caught it. */}
      {controlsMapStyle === 'satellite' && (
        <div
          className="pointer-events-none absolute bottom-1 left-2 z-20 select-none text-[10px] leading-none text-white/70"
          style={{ textShadow: '0 1px 2px black' }}
        >
          {MAP_LAYERS_MENU.imageryCredit}
        </div>
      )}

      {c.droneOverlayNode && (
        <div
          ref={c.overlayRef}
          // Positioned imperatively by positionOverlay; hidden until the first
          // placement so it never appears at the corner for a frame.
          className="absolute left-0 top-0 z-30 will-change-transform"
          style={{ visibility: 'hidden' }}
        >
          {c.droneOverlayNode}
        </div>
      )}

      {c.hoverChip && (
        <DroneHoverChip
          drone={c.hoverChip.drone}
          x={c.hoverChip.x}
          y={c.hoverChip.y}
          containerWidth={c.hoverChip.containerWidth}
        />
      )}

      {c.ui.basemapOffline && (
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5"
          role="status"
          // Clear of whatever floats over the top edge - the dock roster and the
          // manual strip, since #505 slice 7. This banner carries the RETRY
          // button, so leaving it under a `z-30` host hides the only control
          // that gets the basemap back.
          style={
            c.chromeInsets.top > 0
              ? { top: `calc(0.75rem + ${c.chromeInsets.top}px)` }
              : undefined
          }
        >
          <button
            type="button"
            onClick={c.retryBasemap}
            disabled={c.ui.basemapRetrying}
            title={
              c.ui.basemapRetrying
                ? 'Reloading the basemap'
                : 'Basemap tiles could not be loaded. Drones and docks are still live. Click to retry.'
            }
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-status-amber/15 border border-status-amber/40 text-status-amber-fg text-[10px] uppercase tracking-wider transition-colors hover:bg-status-amber/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-status-amber/70 disabled:cursor-default disabled:hover:bg-status-amber/15"
          >
            <span
              className={`size-1.5 rounded-full bg-status-amber ${c.ui.basemapRetrying ? 'motion-safe:animate-pulse' : ''}`}
              aria-hidden
            />
            {c.ui.basemapRetrying ? 'Basemap offline - retrying' : 'Basemap offline - retry'}
          </button>
        </div>
      )}

      <AirspacePanel
        visible={!props.compact && c.ui.showAirspace}
        loading={c.ui.loadingAirspace}
        error={c.ui.airspaceErr}
        tfrs={c.ui.tfrItems}
        notams={c.ui.notamItems}
        notamClearToFly={c.ui.notamClearToFly}
        restrictionsActive={c.restrictionsActive}
        bottomInsetPx={c.chromeInsets.bottom}
        leftInsetPx={c.chromeInsets.left}
      />

      <NfzIsraelPanel
        visible={!props.compact && c.ui.showNfzIsrael}
        loading={c.ui.loadingNfzIsrael}
        error={c.ui.nfzIsraelErr}
        source={c.ui.nfzSource}
        nowMs={c.ui.stalenessNowMs}
        onRetry={c.retryNfzIsrael}
        bottomInsetPx={c.chromeInsets.bottom}
        rightInsetPx={c.chromeInsets.right}
      />

      <DroneMapContextMenu
        contextMenu={c.ui.contextMenu}
        mapDispatchMenu={c.ui.mapDispatchMenu}
        onRequestDispatchHere={c.onRequestDispatchHere}
        onCloseDispatchMenu={() => c.dispatch({ type: 'SET_MAP_DISPATCH_MENU', value: null })}
        onCloseContextMenu={() => c.dispatch({ type: 'SET_CONTEXT_MENU', value: null })}
        measureMenu={c.ui.measureMenu}
        onDeleteMeasurement={c.rangeBearing?.onDeleteLine}
        onCloseMeasureMenu={() => c.dispatch({ type: 'SET_MEASURE_MENU', value: null })}
        onContextAction={c.handleContextAction}
        onOpenVideo={c.onOpenVideo}
        hasTrail={c.hasTrail}
        hasCtrlLink={c.hasCtrlLink}
        followDroneId={c.ui.followDroneId}
        rangeRings={c.contextRangeRings}
      />

      {c.openTrigger && c.ui.triggerCard && (
        <div
          ref={c.triggerCardRef}
          // Positioned imperatively by positionTriggerCard, hidden until the
          // first placement so it never flashes at the corner.
          className="absolute left-0 top-0 z-50 will-change-transform"
          style={{ visibility: 'hidden' }}
        >
          <TriggerPointCard
            // ONE CARD PER DETECTION (re-review, 2026-09-12). `SET_TRIGGER_CARD` swaps the id in
            // a single update, so clicking a second point while the first card is open never
            // unmounts this component - only the prop changes. Its per-card state then carried
            // over: `standingAtOpen` (was there already a proposal when this card opened?) kept
            // the FIRST detection's answer, so a proposal arriving for the second armed Approve
            // immediately and defeated APPROVE_ARM_MS in exactly the triage sequence it exists
            // for; and `approving` survived too, so the second card rendered "Opening bay -
            // launching <its own callsign>" about a launch that belonged to the first.
            key={c.openTrigger.id}
            trigger={c.openTrigger}
            openedAt={c.ui.triggerCard.openedAt}
            onDispatch={c.onDispatchFromTrigger}
            onDismiss={c.onDismissTrigger}
            onApprove={c.onApproveTrigger}
            onReject={c.onRejectTrigger}
            onClose={() => c.dispatch({ type: 'SET_TRIGGER_CARD', value: null })}
          />
        </div>
      )}

      {/* Suppressed in the control minimap: at 280 px the key column and the
          legend would be most of the box, and every verb in them acts on a
          camera the minimap deliberately does not let go of. */}
      {!props.compact && (
      <DroneMapControls
        bottomInsetPx={c.chromeInsets.bottom}
        rightInsetPx={c.chromeInsets.right}
        leftInsetPx={c.chromeInsets.left}
        topInsetPx={c.chromeInsets.top}
        mapStyle={controlsMapStyle}
        map={c.mapInstance}
        isFullscreen={c.ui.isFullscreen}
        isRotated={c.ui.mapRotated}
        viewState={c.controlsView}
        onResetNorth={c.resetNorth}
        onGoToMyLocation={c.goToMyLocation}
        onToggleAirspace={c.handleToggleAirspace}
        onToggleNfzIsrael={c.handleToggleNfzIsrael}
        onToggleMissions={c.handleToggleMissions}
        onToggleAltitudeLabels={c.handleToggleAltitudeLabels}
        onSelectStyle={c.selectStyle}
        satelliteAvailable={c.satelliteAvailable}
        onToggleFullscreen={c.toggleFullscreen}
        onOpenZones={c.onOpenZones}
        assets={c.ui.assetVisibility}
        assetsHeld={c.assetsHeld}
        onToggleAsset={c.handleToggleMapAsset}
        rangeBearing={c.rangeBearing}
        missions={c.missions}
        trailCount={c.legendInputs.trailCount}
        cursorCoords={c.ui.cursorCoords}
        controllers={c.legendInputs.controllers}
        cooperativeTraffic={c.legendInputs.cooperativeTraffic}
        sites={c.legendInputs.sites}
        dockCount={c.legendInputs.dockCount}
        triggerCount={c.triggers.length}
        handoverKey={c.handoverKey}
        selectedDrone={c.selectedDroneControls}
      />
      )}
    </div>
  );
}
